/**
 * IndicatorChart - Recharts-based indicator visualization
 *
 * Features:
 * - Line indicators with toggle visibility
 * - Buy/sell markers with triangle icons
 * - Strategy markers (circles, etc.)
 * - Zoom in/out/reset controls
 * - Crosshairs with axis labels
 * - Custom tooltips
 * - Brush for range selection
 */
import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import {
  ComposedChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Brush,
  Customized,
  ReferenceLine,
  useXAxisScale,
  useYAxisScale,
} from "recharts";
import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels";
import moment from "moment";
import { FaChartBar, FaCommentDots } from "react-icons/fa";
import "./IndicatorChart.css";
import { useChartDrawings } from "./useChartDrawings";
import { DrawToolbar } from "./DrawToolbar";
import { ChartDrawLayer } from "./ChartDrawLayer";
import { AskAIModal } from "./AskAIModal";
import { setAgentHandoff } from "../../utils/aiAgentHandoff";
import {
  useDownsampledData,
  useZoomedDownsampledData,
  downsampleData,
  aggregateOhlcCandles,
} from "./useDownsampledData";
import { useIndicatorClassification } from "./useIndicatorClassification";
import {
  useClusteredSignals,
  getClusterZoomRange,
  clusterSignalsByProximity,
} from "./useClusteredSignals";
import { TradeCluster, TradeClusterTooltip } from "./TradeCluster";

/**
 * Get the user's local timezone abbreviation (e.g., "EST", "PST", "UTC").
 * This works without moment-timezone by using Intl.DateTimeFormat.
 */
const getTimezoneAbbr = () => {
  try {
    // Use Intl to get timezone abbreviation
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZoneName: "short",
    });
    const parts = formatter.formatToParts(new Date());
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    return tzPart?.value || "UTC";
  } catch {
    return "UTC";
  }
};

/**
 * Format a date/timestamp with timezone for AI communication.
 * Always includes timezone abbreviation for clarity.
 */
const formatDateWithTimezone = (dateInput) => {
  const m = moment(dateInput);
  if (!m.isValid()) return "Invalid date";
  return `${m.format("MMM D, YYYY h:mm A")} ${getTimezoneAbbr()}`;
};

// Color palette for indicator lines
const LINE_COLORS = [
  "#8884d8", // Purple
  "#82ca9d", // Green
  "#ffc658", // Yellow
  "#ff7300", // Orange
  "#00C49F", // Teal
  "#FFBB28", // Gold
  "#FF8042", // Coral
  "#0088FE", // Blue
];

// Marker colors - softer, more professional palette
const MARKER_COLORS = {
  buy: "#22c55e", // Softer green
  buyBg: "#166534", // Dark green background
  sell: "#ef4444", // Softer red
  sellBg: "#991b1b", // Dark red background
  marker: "#3b82f6", // Blue
  signal: "#a855f7", // Purple
};

// OHLC candlestick colors
const OHLC_COLORS = {
  up: "#22c55e", // Green for bullish candles
  upWick: "#16a34a", // Slightly darker green for wicks
  down: "#ef4444", // Red for bearish candles
  downWick: "#dc2626", // Slightly darker red for wicks
};

const PRICE_CHART_MARGIN = { top: 10, right: 30, left: 10, bottom: 50 };
const PRICE_CHART_Y_AXIS_WIDTH = 80;
const COMPACT_PRICE_CHART_MARGIN = { top: 6, right: 10, left: 0, bottom: 32 };
const NARROW_COMPACT_PRICE_CHART_MARGIN = {
  top: 6,
  right: 6,
  left: 0,
  bottom: 30,
};
const NARROW_PRICE_CHART_MARGIN = { top: 8, right: 12, left: 4, bottom: 38 };

export const getPriceChartLayout = ({
  compact = false,
  isNarrowChart = false,
} = {}) => {
  if (compact) {
    return {
      margin: isNarrowChart
        ? NARROW_COMPACT_PRICE_CHART_MARGIN
        : COMPACT_PRICE_CHART_MARGIN,
      yAxisWidth: isNarrowChart ? 0 : 44,
      yTickCount: 4,
      hideYAxis: isNarrowChart,
      showBrush: false,
    };
  }

  if (isNarrowChart) {
    return {
      margin: NARROW_PRICE_CHART_MARGIN,
      yAxisWidth: 40,
      yTickCount: 4,
      hideYAxis: false,
      showBrush: true,
    };
  }

  return {
    margin: PRICE_CHART_MARGIN,
    yAxisWidth: PRICE_CHART_Y_AXIS_WIDTH,
    yTickCount: 6,
    hideYAxis: false,
    showBrush: true,
  };
};

export const hasCompleteOhlcPoint = (point, prefix = "") =>
  Number.isFinite(point?.[`${prefix}open`]) &&
  Number.isFinite(point?.[`${prefix}high`]) &&
  Number.isFinite(point?.[`${prefix}low`]) &&
  Number.isFinite(point?.[`${prefix}close`]);

export const getCompleteOhlcCandles = (points, prefix = "") =>
  (points || []).filter((point) => hasCompleteOhlcPoint(point, prefix));

export const getAxisCoordinate = (axis, value) => {
  if (!axis?.scale) return NaN;

  const coordinate = axis.scale(value);
  if (Number.isFinite(coordinate)) return coordinate;

  // Recharts category axes can stringify timestamp keys internally. The native
  // series can still render in that mode, while custom SVG layers need this
  // explicit lookup to avoid silently dropping candles/markers.
  const stringCoordinate = axis.scale(String(value));
  return Number.isFinite(stringCoordinate) ? stringCoordinate : NaN;
};

export const getLinearYCoordinate = (
  value,
  domain,
  chartHeight,
  margin = PRICE_CHART_MARGIN,
) => {
  const numericValue = Number(value);
  const min = Number(domain?.[0]);
  const max = Number(domain?.[1]);
  const plotTop = margin.top;
  const plotBottom = chartHeight - margin.bottom;
  const plotHeight = plotBottom - plotTop;

  if (
    !Number.isFinite(numericValue) ||
    !Number.isFinite(min) ||
    !Number.isFinite(max) ||
    max === min ||
    plotHeight <= 0
  ) {
    return NaN;
  }

  return plotBottom - ((numericValue - min) / (max - min)) * plotHeight;
};

export const getLinearXCoordinate = ({
  timestamp,
  domain,
  chartWidth,
  margin = PRICE_CHART_MARGIN,
  yAxisWidth = PRICE_CHART_Y_AXIS_WIDTH,
}) => {
  const numericTimestamp = Number(timestamp);
  const min = Number(domain?.[0]);
  const max = Number(domain?.[1]);
  const plotLeft = margin.left + yAxisWidth;
  const plotWidth = Math.max(
    1,
    chartWidth - margin.left - margin.right - yAxisWidth,
  );

  if (
    !Number.isFinite(numericTimestamp) ||
    !Number.isFinite(min) ||
    !Number.isFinite(max) ||
    max === min
  ) {
    return NaN;
  }

  return plotLeft + ((numericTimestamp - min) / (max - min)) * plotWidth;
};

export const aggregateOhlcCandlesByTimeBuckets = (
  candles,
  maxBuckets,
  prefix = "",
  domain = null,
) => {
  if (!candles || candles.length === 0) return candles;
  if (!maxBuckets || maxBuckets <= 0 || candles.length <= maxBuckets)
    return candles;

  const sortedCandles = [...candles].sort(
    (a, b) => Number(a.timestamp) - Number(b.timestamp),
  );
  const finiteTimestamps = sortedCandles
    .map((candle) => Number(candle.timestamp))
    .filter(Number.isFinite);
  const min = Number(domain?.[0] ?? finiteTimestamps[0]);
  const max = Number(
    domain?.[1] ?? finiteTimestamps[finiteTimestamps.length - 1],
  );

  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return aggregateOhlcCandles(
      sortedCandles,
      Math.ceil(sortedCandles.length / maxBuckets),
      prefix,
    );
  }

  const buckets = Array.from({ length: maxBuckets }, () => []);
  sortedCandles.forEach((candle) => {
    const timestamp = Number(candle.timestamp);
    if (!Number.isFinite(timestamp)) return;
    const bucketIndex = Math.max(
      0,
      Math.min(
        maxBuckets - 1,
        Math.floor(((timestamp - min) / (max - min)) * maxBuckets),
      ),
    );
    buckets[bucketIndex].push(candle);
  });

  return buckets
    .filter((bucket) => bucket.length > 0)
    .map((bucket) => aggregateOhlcCandles(bucket, bucket.length, prefix)[0])
    .filter(Boolean);
};

export const buildOhlcScatterSeries = ({
  ohlcIndicators = [],
  visibleChartData = [],
  hiddenOhlc = new Set(),
  containerWidth = 800,
  maxRenderedCandles = 200,
  chartHeight = 400,
  yDomain = [0, 100],
  chartMargin = PRICE_CHART_MARGIN,
  yAxisWidth = PRICE_CHART_Y_AXIS_WIDTH,
}) =>
  ohlcIndicators
    .filter((ohlc) => !hiddenOhlc.has(ohlc.name))
    .map((ohlc) => {
      const prefix = `ohlc_${ohlc.name}_`;
      const visibleCandles = getCompleteOhlcCandles(visibleChartData, prefix);
      if (visibleCandles.length === 0) {
        return { name: ohlc.name, points: [] };
      }

      const visibleTimestamps = visibleChartData
        .map((point) => Number(point?.timestamp))
        .filter(Number.isFinite);
      const xDomain =
        visibleTimestamps.length > 1
          ? [
              visibleTimestamps[0],
              visibleTimestamps[visibleTimestamps.length - 1],
            ]
          : [
              visibleCandles[0]?.timestamp,
              visibleCandles[visibleCandles.length - 1]?.timestamp,
            ];
      const renderCandles =
        visibleCandles.length > maxRenderedCandles
          ? aggregateOhlcCandlesByTimeBuckets(
              visibleCandles,
              maxRenderedCandles,
              prefix,
              xDomain,
            )
          : visibleCandles;

      const plotWidth = Math.max(
        1,
        containerWidth - chartMargin.left - chartMargin.right - yAxisWidth,
      );
      const rawCandleWidth =
        (plotWidth / Math.max(renderCandles.length, 1)) * 0.7;
      const candleWidth = Math.max(4, Math.min(rawCandleWidth, 20));

      return {
        name: ohlc.name,
        points: renderCandles
          .filter((point) => hasCompleteOhlcPoint(point, prefix))
          .map((point) => {
            const open = point[`${prefix}open`];
            const high = point[`${prefix}high`];
            const low = point[`${prefix}low`];
            const close = point[`${prefix}close`];

            return {
              ...point,
              isOhlcCandle: true,
              ohlcSeriesName: ohlc.name,
              ohlcOpen: open,
              ohlcHigh: high,
              ohlcLow: low,
              ohlcClose: close,
              ohlcCandleWidth: candleWidth,
              ohlcWickWidth: Math.max(1.25, candleWidth * 0.18),
              ohlcChartHeight: chartHeight,
              ohlcYDomain: yDomain,
              ohlcX: getLinearXCoordinate({
                timestamp: point.timestamp,
                domain: xDomain,
                chartWidth: containerWidth,
                margin: chartMargin,
                yAxisWidth,
              }),
              ohlcChartMargin: chartMargin,
              ohlcColor: point[`${prefix}color`],
            };
          }),
      };
    })
    .filter((series) => series.points.length > 0);

const hasRenderableLineValue = (point, visibleLineIndicators) => {
  if (!point) return false;

  return visibleLineIndicators.some((indicator) => {
    const value = point[indicator.name];
    return (
      value !== undefined && value !== null && Number.isFinite(Number(value))
    );
  });
};

const hasRenderableOhlcValue = (point, visibleOhlcIndicators) => {
  if (!point) return false;
  return visibleOhlcIndicators.some((ohlc) => {
    const prefix = `ohlc_${ohlc.name}_`;
    return hasCompleteOhlcPoint(point, prefix);
  });
};

export const getRenderablePricePaneRange = ({
  data = [],
  visibleLineIndicators = [],
  visibleOhlcIndicators = [],
}) => {
  if (!data || data.length === 0) return null;
  if (visibleLineIndicators.length === 0 && visibleOhlcIndicators.length === 0)
    return null;

  const preferOhlcRange = visibleOhlcIndicators.length > 0;
  let startIndex = null;
  let endIndex = null;

  data.forEach((point, index) => {
    const hasRenderableValue = preferOhlcRange
      ? hasRenderableOhlcValue(point, visibleOhlcIndicators)
      : hasRenderableLineValue(point, visibleLineIndicators);

    if (!hasRenderableValue) return;
    if (startIndex === null) startIndex = index;
    endIndex = index;
  });

  if (startIndex === null || endIndex === null) return null;
  return { startIndex, endIndex };
};

const OhlcCandleShape = ({ cx, cy, payload }) => {
  const candle = payload || {};
  const {
    ohlcOpen,
    ohlcHigh,
    ohlcLow,
    ohlcClose,
    ohlcCandleWidth = 4,
    ohlcWickWidth = 1,
    ohlcChartHeight,
    ohlcYDomain,
    ohlcChartMargin = PRICE_CHART_MARGIN,
  } = candle;

  const x = Number(cx);
  const yClose = Number.isFinite(Number(cy))
    ? Number(cy)
    : getLinearYCoordinate(
        ohlcClose,
        ohlcYDomain,
        ohlcChartHeight,
        ohlcChartMargin,
      );
  const yOpen = getLinearYCoordinate(
    ohlcOpen,
    ohlcYDomain,
    ohlcChartHeight,
    ohlcChartMargin,
  );
  const yHigh = getLinearYCoordinate(
    ohlcHigh,
    ohlcYDomain,
    ohlcChartHeight,
    ohlcChartMargin,
  );
  const yLow = getLinearYCoordinate(
    ohlcLow,
    ohlcYDomain,
    ohlcChartHeight,
    ohlcChartMargin,
  );

  if (
    !Number.isFinite(x) ||
    !Number.isFinite(yOpen) ||
    !Number.isFinite(yHigh) ||
    !Number.isFinite(yLow) ||
    !Number.isFinite(yClose)
  ) {
    return null;
  }

  const isBullish = ohlcClose >= ohlcOpen;
  const bodyColor = isBullish ? OHLC_COLORS.up : OHLC_COLORS.down;
  const wickColor = isBullish ? OHLC_COLORS.upWick : OHLC_COLORS.downWick;
  const bodyTop = Math.min(yOpen, yClose);
  const bodyBottom = Math.max(yOpen, yClose);
  const bodyHeight = Math.max(2, bodyBottom - bodyTop);

  return (
    <g className="ohlc-candle">
      <line
        x1={x}
        y1={yHigh}
        x2={x}
        y2={yLow}
        stroke={wickColor}
        strokeWidth={ohlcWickWidth}
      />
      <rect
        x={x - ohlcCandleWidth / 2}
        y={bodyTop}
        width={ohlcCandleWidth}
        height={bodyHeight}
        fill={bodyColor}
        stroke={wickColor}
        strokeWidth={0.5}
      />
    </g>
  );
};

const OhlcCandlesLayer = ({ ohlcScatterSeries = [] }) => {
  return (
    <g className="ohlc-candlesticks-layer">
      {ohlcScatterSeries.flatMap((series) =>
        series.points.map((point, index) => {
          const cx = point.ohlcX;
          if (!Number.isFinite(cx)) return null;

          return (
            <OhlcCandleShape
              key={`${series.name}-${point.timestamp}-${index}`}
              cx={cx}
              payload={point}
            />
          );
        }),
      )}
    </g>
  );
};

export const renderSignalMarker = ({
  signal,
  markerKey,
  x,
  y,
  setMarkerTooltip,
}) => {
  const isBuy = signal.markerType === "buy";
  const isSell = signal.markerType === "sell";
  const markerColor =
    signal.color || MARKER_COLORS[signal.markerType] || MARKER_COLORS.marker;

  if (isBuy || isSell) {
    const circleR = 10;
    const arrowSize = 5;
    const bgColor = isBuy ? MARKER_COLORS.buyBg : MARKER_COLORS.sellBg;
    const fgColor = isBuy ? MARKER_COLORS.buy : MARKER_COLORS.sell;
    const arrowPoints = isBuy
      ? `${x},${y - arrowSize} ${x - arrowSize},${y + arrowSize * 0.5} ${x + arrowSize},${y + arrowSize * 0.5}`
      : `${x},${y + arrowSize} ${x - arrowSize},${y - arrowSize * 0.5} ${x + arrowSize},${y - arrowSize * 0.5}`;

    return (
      <g
        key={markerKey}
        className={`custom-trade-marker custom-trade-marker-${signal.markerType}`}
        style={{ cursor: "pointer" }}
        onMouseEnter={(e) =>
          setMarkerTooltip({
            visible: true,
            x: e.clientX,
            y: e.clientY,
            data: signal,
          })
        }
      >
        <circle
          cx={x}
          cy={y}
          r={circleR}
          fill={bgColor}
          fillOpacity={0.85}
          stroke={fgColor}
          strokeWidth={2}
        />
        <polygon points={arrowPoints} fill={fgColor} fillOpacity={0.9} />
      </g>
    );
  }

  const shape = (signal.symbol || "circle").toLowerCase();
  const size = 7;
  const commonProps = {
    fill: markerColor,
    fillOpacity: 0.85,
    stroke: "rgba(255,255,255,0.4)",
    strokeWidth: 1.5,
    style: { cursor: "pointer" },
    onMouseEnter: (e) =>
      setMarkerTooltip({
        visible: true,
        x: e.clientX,
        y: e.clientY,
        data: signal,
      }),
  };

  switch (shape) {
    case "star": {
      const outerR = size + 2;
      const innerR = size * 0.4;
      const points = [];
      for (let i = 0; i < 10; i++) {
        const r = i % 2 === 0 ? outerR : innerR;
        const angle = Math.PI / 2 + (i * Math.PI) / 5;
        points.push(`${x + r * Math.cos(angle)},${y - r * Math.sin(angle)}`);
      }
      return (
        <polygon key={markerKey} points={points.join(" ")} {...commonProps} />
      );
    }
    case "diamond": {
      const d = size + 1;
      const diamondPoints = `${x},${y - d} ${x + d},${y} ${x},${y + d} ${x - d},${y}`;
      return (
        <polygon key={markerKey} points={diamondPoints} {...commonProps} />
      );
    }
    case "triangle":
    case "arrowup": {
      const t = size + 1;
      const triPoints = `${x},${y - t} ${x - t},${y + t * 0.7} ${x + t},${y + t * 0.7}`;
      return <polygon key={markerKey} points={triPoints} {...commonProps} />;
    }
    case "triangledown":
    case "arrowdown": {
      const t = size + 1;
      const triPoints = `${x},${y + t} ${x - t},${y - t * 0.7} ${x + t},${y - t * 0.7}`;
      return <polygon key={markerKey} points={triPoints} {...commonProps} />;
    }
    case "square": {
      const s = size;
      return (
        <rect
          key={markerKey}
          x={x - s}
          y={y - s}
          width={s * 2}
          height={s * 2}
          {...commonProps}
        />
      );
    }
    case "cross":
    case "x": {
      const c = size;
      return (
        <g key={markerKey} {...commonProps}>
          <line
            x1={x - c}
            y1={y - c}
            x2={x + c}
            y2={y + c}
            stroke={markerColor}
            strokeWidth={3}
          />
          <line
            x1={x + c}
            y1={y - c}
            x2={x - c}
            y2={y + c}
            stroke={markerColor}
            strokeWidth={3}
          />
        </g>
      );
    }
    case "plus": {
      const p = size;
      return (
        <g key={markerKey} {...commonProps}>
          <line
            x1={x - p}
            y1={y}
            x2={x + p}
            y2={y}
            stroke={markerColor}
            strokeWidth={3}
          />
          <line
            x1={x}
            y1={y - p}
            x2={x}
            y2={y + p}
            stroke={markerColor}
            strokeWidth={3}
          />
        </g>
      );
    }
    case "circle":
    default:
      return <circle key={markerKey} cx={x} cy={y} r={size} {...commonProps} />;
  }
};

export function CustomMarkersLayer({
  processedSignals,
  signalsForClustering,
  shouldClusterSignals,
  zoomRange,
  displayChartDataLength,
  visibleMarkers,
  clusterRadius,
  setMarkerTooltip,
  handleClusterClick,
  handleClusterHover,
  handleClusterLeave,
}) {
  const xScale = useXAxisScale();
  const yScale = useYAxisScale();

  if (!xScale || !yScale) return null;

  const xAxis = { scale: xScale };
  const yAxis = { scale: yScale };
  const startIdx = zoomRange.startIndex ?? 0;
  const endIdx = zoomRange.endIndex ?? displayChartDataLength - 1;

  if (
    shouldClusterSignals &&
    signalsForClustering &&
    signalsForClustering.length > 0
  ) {
    const allSignalsWithPixels = signalsForClustering.map((signal) => ({
      ...signal,
      pixelX: getAxisCoordinate(xAxis, signal.chartTimestamp),
      pixelY: getAxisCoordinate(yAxis, signal.chartY),
    }));
    const signalsWithPixels = allSignalsWithPixels.filter(
      (s) => Number.isFinite(s.pixelX) && Number.isFinite(s.pixelY),
    );
    const nanCount = allSignalsWithPixels.length - signalsWithPixels.length;

    // Recharts scales can lag by a render frame during zoom changes. If most
    // markers do not map yet, skip this frame instead of drawing bad clusters.
    if (nanCount > 0 && nanCount / allSignalsWithPixels.length > 0.2) {
      if (process.env.NODE_ENV === "development") {
        console.warn(
          `[Clustering] ${nanCount}/${allSignalsWithPixels.length} signals have NaN pixels (${Math.round((nanCount / allSignalsWithPixels.length) * 100)}%) - scales likely stale, skipping render`,
        );
      }
      return <g className="custom-markers-layer clustered-stale" />;
    }

    if (process.env.NODE_ENV === "development") {
      if (nanCount > 0) {
        console.warn(
          `[Clustering] ${nanCount}/${allSignalsWithPixels.length} signals lost to NaN pixel coords`,
        );
      }
      console.log(
        `[Clustering] ${signalsForClustering.length} input to ${signalsWithPixels.length} valid pixels, radius: ${clusterRadius}px`,
      );
    }

    const clusters = clusterSignalsByProximity(
      signalsWithPixels,
      clusterRadius,
    );
    const actualClusters = [];
    const individualSignals = [];

    clusters.forEach((cluster) => {
      if (cluster.signals.length >= 2) {
        actualClusters.push(cluster);
      } else {
        individualSignals.push(...cluster.signals);
      }
    });

    if (process.env.NODE_ENV === "development") {
      console.log(
        `[Clustering] ${actualClusters.length} clusters (${actualClusters.reduce((s, c) => s + c.signals.length, 0)} signals), ${individualSignals.length} individuals`,
      );
    }

    return (
      <g className="custom-markers-layer clustered">
        {actualClusters.map((cluster, idx) => (
          <TradeCluster
            key={`cluster-${idx}`}
            cluster={cluster}
            onClick={handleClusterClick}
            onHover={handleClusterHover}
            onMouseLeave={handleClusterLeave}
          />
        ))}
        {individualSignals.map((signal, idx) =>
          renderSignalMarker({
            signal,
            markerKey: `individual-${idx}`,
            x: signal.pixelX,
            y: signal.pixelY,
            setMarkerTooltip,
          }),
        )}
      </g>
    );
  }

  return (
    <g className="custom-markers-layer">
      {processedSignals.map((signal, idx) => {
        if (signal.chartIndex < startIdx || signal.chartIndex > endIdx)
          return null;

        const isBuy = signal.markerType === "buy";
        const isSell = signal.markerType === "sell";
        if (isBuy && !visibleMarkers.has("buy")) return null;
        if (isSell && !visibleMarkers.has("sell")) return null;
        if (
          signal.markerType === "marker" &&
          signal.name &&
          !visibleMarkers.has(signal.name)
        )
          return null;

        const x = getAxisCoordinate(xAxis, signal.chartTimestamp);
        const y = getAxisCoordinate(yAxis, signal.chartY);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

        return renderSignalMarker({
          signal,
          markerKey: `signal-${idx}`,
          x,
          y,
          setMarkerTooltip,
        });
      })}
    </g>
  );
}

function IndicatorChart({
  indicatorData = [],
  signalData = [],
  resolution = "D",
  isIntraday = false,
  selectedAsset = null,
  height = 400,
  onAskAI = null, // Callback when user clicks "Ask AI" with drawings
  aiStrategyId = null, // AI strategy ID for linking to existing conversation
  revisionId = null, // Strategy revision ID
  backtestId = null, // Backtest ID for context
  backtestVersion = null, // Backtest version for AI context
  strategyName = null, // Strategy name for AI context
  symbolSelector = null, // Optional: render element for symbol picker (inline in toolbar)
  compact = false, // Compact embedding for chat/message cards
}) {
  // Track which indicators are visible
  const [hiddenIndicators, setHiddenIndicators] = useState(new Set());

  // Track which marker types are VISIBLE
  // Buy/Sell ON by default, other markers OFF by default
  const [visibleMarkers, setVisibleMarkers] = useState(
    new Set(["buy", "sell"]),
  );

  // Track which OHLC series are visible (all visible by default)
  const [hiddenOhlc, setHiddenOhlc] = useState(new Set());

  // Zoom state - index range for visible data
  const [zoomRange, setZoomRange] = useState({
    startIndex: null,
    endIndex: null,
  });

  // Crosshair state - includes pixel position and actual values
  const [crosshair, setCrosshair] = useState({
    x: null, // pixel X
    y: null, // pixel Y
    timestamp: null, // actual timestamp value
    value: null, // actual Y value
    visible: false,
  });

  // Marker tooltip state
  const [markerTooltip, setMarkerTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    data: null,
  });

  // Cluster tooltip state (for trade clustering when zoomed out)
  const [clusterTooltip, setClusterTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    cluster: null,
  });

  // Track if initial zoom has been applied (for fast initial load)
  const [initialZoomApplied, setInitialZoomApplied] = useState(false);
  // Mobile: entire legend panel collapsed by default to maximize chart space
  const [legendExpanded, setLegendExpanded] = useState(false);

  // Configuration for performance optimizations
  // Video-game LOD: the further you zoom out, the more aggressive the simplification
  const PERFORMANCE_CONFIG = {
    INITIAL_VISIBLE_POINTS: 80, // Start zoomed in enough for mobile users to read trades and moving averages
    MAX_INDIVIDUAL_SIGNALS: 400, // Cluster only when many visible signals — keeps trades visible at most zoom levels
    CLUSTER_RADIUS: 15, // Pixels - small radius = small clusters that grow naturally as zoom decreases
    MAX_RENDERED_CANDLES: 150, // LOD: max OHLC candles to render while zoomed out
  };

  // Text annotation input state
  const [textInputValue, setTextInputValue] = useState("");

  // Ask AI modal state
  const [isAskAIModalOpen, setIsAskAIModalOpen] = useState(false);

  // Hover-to-delete state - which drawing is currently hovered
  // This is detected via mouse position, not SVG hover events (Recharts blocks those)
  const [hoveredDrawingId, setHoveredDrawingId] = useState(null);

  // Chart container ref for crosshair calculations
  const chartContainerRef = useRef(null);

  // Drawing state and actions
  const {
    isDrawMode,
    activeTool,
    drawings,
    currentDrawing,
    isDrawing,
    pendingTextDrawing,
    enterDrawMode,
    exitDrawMode,
    selectTool,
    startDrawing,
    updateDrawing,
    finishDrawing,
    removeDrawing, // For deleting individual drawings on hover
    clearAllDrawings,
    confirmTextDrawing,
    cancelTextDrawing,
    captureScales,
    serializeForAI,
  } = useChartDrawings(backtestId); // Persist drawings per backtest in localStorage

  // Get line indicators only
  const lineIndicators = useMemo(() => {
    return indicatorData.filter((ind) => ind.type === "line");
  }, [indicatorData]);

  // Get OHLC indicators
  const ohlcIndicators = useMemo(() => {
    return indicatorData.filter((ind) => ind.type === "ohlc");
  }, [indicatorData]);

  // Build unified chart data from all indicator lines and OHLC data
  const chartData = useMemo(() => {
    // Need either line or OHLC data
    if (lineIndicators.length === 0 && ohlcIndicators.length === 0) return [];

    const timeMap = new Map();

    // Add line indicator points
    lineIndicators.forEach((indicator) => {
      if (!indicator.points) return;
      indicator.points.forEach((point) => {
        const timestamp = point.time * 1000;
        if (!timeMap.has(timestamp)) {
          timeMap.set(timestamp, { timestamp, time: point.time });
        }
        timeMap.get(timestamp)[indicator.name] = point.value;
      });
    });

    // Add OHLC data points
    ohlcIndicators.forEach((ohlc) => {
      if (!ohlc.points) return;
      ohlc.points.forEach((point) => {
        const timestamp = point.time * 1000;
        if (!timeMap.has(timestamp)) {
          timeMap.set(timestamp, { timestamp, time: point.time });
        }
        // Store OHLC data with prefixed keys to avoid collisions
        const entry = timeMap.get(timestamp);
        entry[`ohlc_${ohlc.name}_open`] = point.open;
        entry[`ohlc_${ohlc.name}_high`] = point.high;
        entry[`ohlc_${ohlc.name}_low`] = point.low;
        entry[`ohlc_${ohlc.name}_close`] = point.close;
        entry[`ohlc_${ohlc.name}_color`] = point.color;
      });
    });

    return Array.from(timeMap.values()).sort(
      (a, b) => a.timestamp - b.timestamp,
    );
  }, [lineIndicators, ohlcIndicators]);

  // PERFORMANCE: Track container width for downsampling calculations
  const [containerWidth, setContainerWidth] = useState(800);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const width = entry.contentRect.width;
        if (width > 0) {
          setContainerWidth(width);
        }
      }
    });

    observer.observe(chartContainerRef.current);

    // Set initial width
    const initialWidth =
      chartContainerRef.current.getBoundingClientRect().width;
    if (initialWidth > 0) {
      setContainerWidth(initialWidth);
    }

    return () => observer.disconnect();
  }, []);

  const isNarrowChart = containerWidth > 0 && containerWidth < 520;
  const isCompactChrome = compact || isNarrowChart;
  const priceChartLayout = useMemo(
    () => getPriceChartLayout({ compact, isNarrowChart }),
    [compact, isNarrowChart],
  );
  const priceChartMargin = priceChartLayout.margin;
  const priceChartYAxisWidth = priceChartLayout.yAxisWidth;
  const maxRenderedCandles = isNarrowChart
    ? 42
    : PERFORMANCE_CONFIG.MAX_RENDERED_CANDLES;

  // PERFORMANCE: Use full dataset for the Brush so zoom reveals full detail.
  // The Brush + initial zoom (INITIAL_VISIBLE_POINTS) controls how many points
  // actually render at any given time. When zoomed out fully, this may be heavy
  // but the trade-off is that zooming in always shows minute-level granularity.
  const displayChartData = useMemo(() => {
    if (!chartData || chartData.length === 0) return chartData;

    // For very large datasets (50k+), still apply moderate downsampling to keep
    // the Brush responsive. For typical backtest sizes (<10k), use full data.
    const maxPointsForFullChart = 10000;

    if (chartData.length <= maxPointsForFullChart) {
      return chartData;
    }

    // Only downsample truly massive datasets
    const firstLineIndicator = lineIndicators[0]?.name;
    const valueKey = firstLineIndicator || "close";
    const downsampled = downsampleData(
      chartData,
      maxPointsForFullChart,
      valueKey,
    );

    if (
      process.env.NODE_ENV === "development" &&
      chartData.length !== downsampled.length
    ) {
      console.log(
        `[IndicatorChart] Downsampled FULL dataset: ${chartData.length} → ${downsampled.length} points`,
      );
    }

    return downsampled;
  }, [chartData, lineIndicators]);

  // Auto-classify indicators into price pane vs separate indicator panes
  // RSI, MACD, etc. get their own panes; EMA, Bollinger bands overlay on price
  // Uses full chartData for stable classification regardless of zoom level
  const {
    needsSeparatePanes,
    pricePaneIndicators,
    indicatorPanes,
    totalPanes,
  } = useIndicatorClassification(
    lineIndicators,
    ohlcIndicators,
    chartData,
    zoomRange,
    hiddenIndicators,
  );

  const visiblePricePaneIndicators = useMemo(
    () => pricePaneIndicators.filter((ind) => !hiddenIndicators.has(ind.name)),
    [pricePaneIndicators, hiddenIndicators],
  );

  const visibleOhlcIndicators = useMemo(
    () => ohlcIndicators.filter((ohlc) => !hiddenOhlc.has(ohlc.name)),
    [ohlcIndicators, hiddenOhlc],
  );

  const pricePaneDataRange = useMemo(
    () =>
      getRenderablePricePaneRange({
        data: displayChartData,
        visibleLineIndicators: visiblePricePaneIndicators,
        visibleOhlcIndicators,
      }),
    [displayChartData, visiblePricePaneIndicators, visibleOhlcIndicators],
  );

  // Initialize or reset zoom range when displayChartData becomes available
  // PERFORMANCE: Start zoomed in to last N points for fast initial render
  // This ensures the Brush has proper initial values for controlled mode
  // NOTE: We use displayChartData.length since that's what the Brush operates on
  useEffect(() => {
    if (displayChartData.length > 0) {
      // Initialize if null - start zoomed in for fast initial load
      if (zoomRange.startIndex === null || zoomRange.endIndex === null) {
        // If we have lots of data, start zoomed into the last N points
        // This prevents the initial render from choking on too many visible points
        const rangeEnd =
          pricePaneDataRange?.endIndex ?? displayChartData.length - 1;
        const rangeStart = pricePaneDataRange?.startIndex ?? 0;
        const rangeLength = rangeEnd - rangeStart + 1;

        if (
          rangeLength > PERFORMANCE_CONFIG.INITIAL_VISIBLE_POINTS &&
          !initialZoomApplied
        ) {
          const startIdx =
            rangeEnd - PERFORMANCE_CONFIG.INITIAL_VISIBLE_POINTS + 1;
          setZoomRange({
            startIndex: startIdx,
            endIndex: rangeEnd,
          });
          setInitialZoomApplied(true);
        } else {
          setZoomRange({
            startIndex: rangeStart,
            endIndex: rangeEnd,
          });
        }
      }
      // Reset if current range is out of bounds (data changed)
      else if (zoomRange.endIndex >= displayChartData.length) {
        setZoomRange({
          startIndex: Math.min(
            zoomRange.startIndex,
            displayChartData.length - 1,
          ),
          endIndex: displayChartData.length - 1,
        });
      }
    }
  }, [
    displayChartData.length,
    zoomRange.startIndex,
    zoomRange.endIndex,
    initialZoomApplied,
    pricePaneDataRange,
  ]);

  // Process ALL signals (trades + markers)
  // NOTE: Uses displayChartData for index mapping so signals align with downsampled chart
  const processedSignals = useMemo(() => {
    if (
      !signalData ||
      signalData.length === 0 ||
      displayChartData.length === 0
    ) {
      return [];
    }

    // Binary search helper - uses displayChartData for consistent indices
    const findClosestDataPoint = (targetTimestamp) => {
      let left = 0;
      let right = displayChartData.length - 1;

      while (left < right) {
        const mid = Math.floor((left + right) / 2);
        if (displayChartData[mid].timestamp < targetTimestamp) {
          left = mid + 1;
        } else {
          right = mid;
        }
      }

      const candidates = [left];
      if (left > 0) candidates.push(left - 1);
      if (left < displayChartData.length - 1) candidates.push(left + 1);

      let closestIdx = left;
      let closestDiff = Infinity;
      for (const idx of candidates) {
        const diff = Math.abs(
          displayChartData[idx].timestamp - targetTimestamp,
        );
        if (diff < closestDiff) {
          closestDiff = diff;
          closestIdx = idx;
        }
      }

      return { point: displayChartData[closestIdx], index: closestIdx };
    };

    // BUGFIX: Markers should NOT depend on which line indicators are visible.
    // Try ALL indicators (visible or not) to find a valid Y value for positioning.
    // This prevents buy/sell markers from disappearing when user hides indicator lines.
    // Also support OHLC-only charts by using OHLC close price for Y positioning.
    if (lineIndicators.length === 0 && ohlcIndicators.length === 0) return [];

    // Flatten signal data: some signals have direct time/value, others have points arrays
    // Trade signals from BacktestIndicatorChart use points arrays: { name, type, points: [...] }
    // Marker signals may have direct time property: { time, value, type, ... }
    const flattenedSignals = signalData.flatMap((signal) => {
      // If signal has a points array, expand each point into a separate signal
      if (signal.points && Array.isArray(signal.points)) {
        return signal.points.map((point) => ({
          ...point,
          // Preserve parent signal metadata
          name: signal.name,
          parentType: signal.type, // 'trade' or 'marker'
        }));
      }
      // Otherwise, signal already has direct time/value properties
      return [signal];
    });

    return flattenedSignals
      .map((signal) => {
        const signalTimestamp = signal.time * 1000;
        const { point: closestPoint, index } =
          findClosestDataPoint(signalTimestamp);
        if (!closestPoint) return null;

        // Try to find Y value - prefer signal's own value (e.g., trade execution price),
        // then fall back to ANY indicator (visible or hidden), then OHLC close price
        let yValue;

        // First, use the signal's own value if it has one (trades have execution price)
        if (signal.value !== undefined && signal.value !== null) {
          yValue = signal.value;
        }

        // Fall back to indicator values if signal doesn't have its own value
        if (yValue === undefined) {
          const visibleIndicators = lineIndicators.filter(
            (ind) => !hiddenIndicators.has(ind.name),
          );

          // First try visible line indicators
          for (const ind of visibleIndicators) {
            if (closestPoint[ind.name] !== undefined) {
              yValue = closestPoint[ind.name];
              break;
            }
          }
        }

        // If no visible indicator has data, try hidden line indicators
        if (yValue === undefined) {
          const hiddenIndicatorsList = lineIndicators.filter((ind) =>
            hiddenIndicators.has(ind.name),
          );
          for (const ind of hiddenIndicatorsList) {
            if (closestPoint[ind.name] !== undefined) {
              yValue = closestPoint[ind.name];
              break;
            }
          }
        }

        // If still no Y value from lines, try OHLC close prices
        if (yValue === undefined) {
          for (const ohlc of ohlcIndicators) {
            const closeValue = closestPoint[`ohlc_${ohlc.name}_close`];
            if (closeValue !== undefined) {
              yValue = closeValue;
              break;
            }
          }
        }

        // If still no Y value, skip this signal (no indicator data at this point)
        if (yValue === undefined) return null;

        // Determine marker type
        // Handle all trade sides from LumiBot Order.OrderSide enum
        // Also handle signals that have a 'side' field but no 'type' field
        // Note: parentType comes from flattened signals that had points arrays (e.g., trade signals)
        let markerType = "marker";
        const side = (signal.side || "").toLowerCase();

        if (signal.type === "trade" || signal.parentType === "trade" || side) {
          // Buy-side trades: buy, buy_to_open, buy_to_close, buy_to_cover
          if (
            side === "buy" ||
            side === "buy_to_open" ||
            side === "buy_to_close" ||
            side === "buy_to_cover"
          ) {
            markerType = "buy";
          }
          // Sell-side trades: sell, sell_to_open, sell_to_close, sell_short
          else if (
            side === "sell" ||
            side === "sell_to_open" ||
            side === "sell_to_close" ||
            side === "sell_short"
          ) {
            markerType = "sell";
          }
        } else if (signal.type === "marker") {
          markerType = "marker";
        }

        return {
          ...signal,
          chartTimestamp: closestPoint.timestamp,
          chartY: yValue,
          chartIndex: index,
          markerType,
        };
      })
      .filter(Boolean);
  }, [
    signalData,
    displayChartData,
    lineIndicators,
    ohlcIndicators,
    hiddenIndicators,
  ]);

  // PERFORMANCE: Calculate downsampled data for the visible range
  // This reduces 50,000 points to ~2,000 while preserving visual shape
  const downsampledChartData = useMemo(() => {
    if (!chartData || chartData.length === 0) return chartData;

    // Get the visible range
    const startIdx = zoomRange.startIndex ?? 0;
    const endIdx = zoomRange.endIndex ?? chartData.length - 1;
    const visibleCount = endIdx - startIdx + 1;

    // Target ~2000 points max for smooth rendering
    // Below this threshold, no downsampling needed
    const MAX_DISPLAY_POINTS = 2000;

    if (visibleCount <= MAX_DISPLAY_POINTS) {
      // No downsampling needed
      return chartData;
    }

    // We need downsampling - the actual downsampling happens in OHLC rendering
    // because we need the scales for proper visualization
    // This memo just flags that downsampling is needed
    return chartData;
  }, [chartData, zoomRange]);

  // Track whether we should show clusters or individual signals
  // IMPORTANT: Count only VISIBLE signals (matching signalsForClustering filter)
  // Previously this counted ALL signal types (including hidden EntryLong, ExitLong, etc.)
  // which inflated the count and triggered clustering even when few buy/sell signals were visible.
  const shouldClusterSignals = useMemo(() => {
    if (!processedSignals || processedSignals.length === 0) return false;

    const startIdx = zoomRange.startIndex ?? 0;
    const endIdx = zoomRange.endIndex ?? displayChartData.length - 1;

    // Count only signals that would actually render (same filter as signalsForClustering)
    const visibleSignalCount = processedSignals.filter((signal) => {
      if (signal.chartIndex < startIdx || signal.chartIndex > endIdx)
        return false;
      const isBuy = signal.markerType === "buy";
      const isSell = signal.markerType === "sell";
      if (isBuy && !visibleMarkers.has("buy")) return false;
      if (isSell && !visibleMarkers.has("sell")) return false;
      if (
        signal.markerType === "marker" &&
        signal.name &&
        !visibleMarkers.has(signal.name)
      )
        return false;
      return true;
    }).length;

    const shouldCluster =
      visibleSignalCount > PERFORMANCE_CONFIG.MAX_INDIVIDUAL_SIGNALS;

    if (process.env.NODE_ENV === "development") {
      console.log(
        `[IndicatorChart] Visible signals: ${visibleSignalCount}, threshold: ${PERFORMANCE_CONFIG.MAX_INDIVIDUAL_SIGNALS}, clustering: ${shouldCluster}`,
      );
    }

    return shouldCluster;
  }, [processedSignals, zoomRange, displayChartData.length, visibleMarkers]);

  // PERFORMANCE: Get clustered signals when there are too many to render individually
  // The clustering hook needs axis scales which are only available inside Customized
  // So we pass the scales dynamically when rendering
  // This memo just prepares the signals for clustering
  const signalsForClustering = useMemo(() => {
    if (!shouldClusterSignals || !processedSignals) return null;

    // Filter signals to visible range and apply marker visibility
    // Use displayChartData for consistent indices with the chart
    const startIdx = zoomRange.startIndex ?? 0;
    const endIdx = zoomRange.endIndex ?? displayChartData.length - 1;

    return processedSignals.filter((signal) => {
      // Range filter
      if (signal.chartIndex < startIdx || signal.chartIndex > endIdx)
        return false;

      // Visibility filter
      const isBuy = signal.markerType === "buy";
      const isSell = signal.markerType === "sell";
      if (isBuy && !visibleMarkers.has("buy")) return false;
      if (isSell && !visibleMarkers.has("sell")) return false;
      if (
        signal.markerType === "marker" &&
        signal.name &&
        !visibleMarkers.has(signal.name)
      )
        return false;

      return true;
    });
  }, [
    shouldClusterSignals,
    processedSignals,
    zoomRange,
    displayChartData.length,
    visibleMarkers,
  ]);

  // Calculate Y-axis domain for the PRICE PANE (includes overlay lines and OHLC)
  // When separate panes exist, this excludes indicators like RSI/MACD from the domain
  // Uses displayChartData (downsampled) for consistent indices with the chart
  const yDomain = useMemo(() => {
    if (displayChartData.length === 0) return [0, 100];

    // Need at least one visible data series
    if (
      visiblePricePaneIndicators.length === 0 &&
      visibleOhlcIndicators.length === 0
    )
      return [0, 100];

    // Apply zoom range if set - use displayChartData for consistent indices
    const startIdx = zoomRange.startIndex ?? 0;
    const endIdx = zoomRange.endIndex ?? displayChartData.length - 1;
    const visibleData = displayChartData.slice(startIdx, endIdx + 1);

    let min = Infinity;
    let max = -Infinity;

    visibleData.forEach((point) => {
      // Line indicator values
      visiblePricePaneIndicators.forEach((ind) => {
        const val = point[ind.name];
        if (val !== undefined && val !== null) {
          min = Math.min(min, val);
          max = Math.max(max, val);
        }
      });

      // OHLC high/low values
      visibleOhlcIndicators.forEach((ohlc) => {
        const high = point[`ohlc_${ohlc.name}_high`];
        const low = point[`ohlc_${ohlc.name}_low`];
        if (high !== undefined && high !== null) {
          max = Math.max(max, high);
        }
        if (low !== undefined && low !== null) {
          min = Math.min(min, low);
        }
      });
    });

    if (!isFinite(min) || !isFinite(max)) return [0, 100];

    const padding = (max - min) * 0.1 || 1;
    return [min - padding, max + padding];
  }, [
    displayChartData,
    visiblePricePaneIndicators,
    visibleOhlcIndicators,
    zoomRange,
  ]);

  // Visible (zoomed) chart data for indicator panes
  // Indicator panes don't have a Brush, so they use sliced data
  const visibleChartData = useMemo(() => {
    if (!displayChartData || displayChartData.length === 0)
      return displayChartData;
    const startIdx = zoomRange.startIndex ?? 0;
    const endIdx = zoomRange.endIndex ?? displayChartData.length - 1;
    return displayChartData.slice(startIdx, endIdx + 1);
  }, [displayChartData, zoomRange]);

  const priceChartHeight = height - (isCompactChrome ? 48 : 60);
  const ohlcScatterSeries = useMemo(
    () =>
      buildOhlcScatterSeries({
        ohlcIndicators,
        visibleChartData,
        hiddenOhlc,
        containerWidth,
        maxRenderedCandles,
        chartHeight: priceChartHeight,
        yDomain,
        chartMargin: priceChartMargin,
        yAxisWidth: priceChartYAxisWidth,
      }),
    [
      ohlcIndicators,
      visibleChartData,
      hiddenOhlc,
      containerWidth,
      maxRenderedCandles,
      priceChartHeight,
      yDomain,
      priceChartMargin,
      priceChartYAxisWidth,
    ],
  );

  // Calculate total chart height when indicator panes are present
  // Increase height to accommodate separate indicator panes
  const INDICATOR_PANE_HEIGHT = 180;
  const visibleIndicatorPanes = indicatorPanes.filter((p) => !p.allHidden);
  const totalChartHeight =
    needsSeparatePanes && visibleIndicatorPanes.length > 0
      ? height + visibleIndicatorPanes.length * INDICATOR_PANE_HEIGHT
      : height;

  const visibleDateRangeDays = useMemo(() => {
    if (!visibleChartData || visibleChartData.length < 2) return 0;
    const first = Number(visibleChartData[0]?.timestamp);
    const last = Number(
      visibleChartData[visibleChartData.length - 1]?.timestamp,
    );
    if (!Number.isFinite(first) || !Number.isFinite(last)) return 0;
    return Math.abs(last - first) / (1000 * 60 * 60 * 24);
  }, [visibleChartData]);

  // Format X-axis tick
  const formatXAxis = useCallback(
    (timestamp) => {
      if (!timestamp) return "";
      const m = moment(timestamp);
      if (!m.isValid()) return "";
      if (visibleDateRangeDays > 365 * 2) return m.format("YYYY");
      if (visibleDateRangeDays > 180) return m.format("MMM 'YY");
      return isIntraday ? m.format("M/D HH:mm") : m.format("M/D");
    },
    [isIntraday, visibleDateRangeDays],
  );

  // Format Y-axis tick - show full numbers with commas
  const formatYAxis = useCallback((value) => {
    if (value === undefined || value === null) return "";
    // Format with commas, appropriate decimal places
    if (Number.isInteger(value)) {
      return value.toLocaleString("en-US");
    }
    // For decimals, show 2 decimal places
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  // Zoom handlers - use displayChartData for consistent indices
  const handleZoomIn = useCallback(() => {
    const dataLength = displayChartData.length;
    if (dataLength < 10) return;

    const currentStart = zoomRange.startIndex ?? 0;
    const currentEnd = zoomRange.endIndex ?? dataLength - 1;
    const range = currentEnd - currentStart;
    const newRange = Math.max(10, Math.floor(range * 0.7));
    const center = Math.floor((currentStart + currentEnd) / 2);
    const newStart = Math.max(0, center - Math.floor(newRange / 2));
    const newEnd = Math.min(dataLength - 1, newStart + newRange);

    setZoomRange({ startIndex: newStart, endIndex: newEnd });
  }, [displayChartData.length, zoomRange]);

  const handleZoomOut = useCallback(() => {
    const dataLength = displayChartData.length;
    if (dataLength === 0) return;

    const currentStart = zoomRange.startIndex ?? 0;
    const currentEnd = zoomRange.endIndex ?? dataLength - 1;
    const range = currentEnd - currentStart;
    const newRange = Math.min(dataLength - 1, Math.floor(range * 1.4));
    const center = Math.floor((currentStart + currentEnd) / 2);
    const newStart = Math.max(0, center - Math.floor(newRange / 2));
    const newEnd = Math.min(dataLength - 1, newStart + newRange);

    setZoomRange({ startIndex: newStart, endIndex: newEnd });
  }, [displayChartData.length, zoomRange]);

  const handleZoomReset = useCallback(() => {
    setZoomRange(
      pricePaneDataRange || {
        startIndex: 0,
        endIndex: Math.max(displayChartData.length - 1, 0),
      },
    );
  }, [displayChartData.length, pricePaneDataRange]);

  // Handle brush change for zoom
  const handleBrushChange = useCallback((range) => {
    if (
      range &&
      range.startIndex !== undefined &&
      range.endIndex !== undefined
    ) {
      setZoomRange({ startIndex: range.startIndex, endIndex: range.endIndex });
    }
  }, []);

  // Handle cluster click - zoom in to show individual signals
  // Uses displayChartData for consistent indices with the chart
  const handleClusterClick = useCallback(
    (e, cluster) => {
      if (!cluster || !displayChartData.length) return;

      // Get the zoom range that will show this cluster's signals individually
      const newRange = getClusterZoomRange(cluster, displayChartData, 50);
      if (newRange) {
        setZoomRange(newRange);
        // Hide cluster tooltip when zooming
        setClusterTooltip({ visible: false, x: 0, y: 0, cluster: null });
      }
    },
    [displayChartData],
  );

  // Handle cluster hover - show tooltip
  const handleClusterHover = useCallback((e, cluster) => {
    setClusterTooltip({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      cluster,
    });
  }, []);

  // Handle cluster mouse leave
  const handleClusterLeave = useCallback(() => {
    setClusterTooltip({ visible: false, x: 0, y: 0, cluster: null });
  }, []);

  // Store axis scales for crosshair value calculation
  const axisScalesRef = useRef({ xScale: null, yScale: null });

  /**
   * Detect which drawing (if any) contains the given pixel coordinates.
   *
   * WHY THIS EXISTS:
   * - Recharts intercepts mouse events at the chart level
   * - Native SVG hover events don't reach elements inside Customized components
   * - We detect hover via pixel position instead of relying on SVG events
   *
   * DETECTION WORKS IN DRAW MODE TOO:
   * - User requested X buttons visible even while drawing
   * - Allows deleting existing drawings without exiting draw mode
   *
   * @param {number} chartX - Pixel X coordinate from Recharts event
   * @param {number} chartY - Pixel Y coordinate from Recharts event
   * @returns {string|null} Drawing ID if a drawing is at this point, null otherwise
   */
  const findDrawingAtPoint = useCallback(
    (chartX, chartY) => {
      if (!drawings.length) return null;

      const { xScale, yScale } = axisScalesRef.current;
      if (!xScale || !yScale) return null;

      // Larger thresholds for easier targeting
      const PIN_THRESHOLD = 25; // Pins need bigger target since they're small
      const SHAPE_THRESHOLD = 20; // General threshold for shapes
      const LINE_THRESHOLD = 18; // Lines/arrows

      for (const drawing of drawings) {
        const { type, start, end } = drawing;

        // Convert data coordinates to pixels
        const x1 = xScale(start.timestamp);
        const y1 = yScale(start.value);
        const x2 = xScale(end.timestamp);
        const y2 = yScale(end.value);

        if (isNaN(x1) || isNaN(y1) || isNaN(x2) || isNaN(y2)) continue;

        switch (type) {
          case "pin": {
            // Pin: check distance from center - larger threshold for easier targeting
            const distance = Math.sqrt((chartX - x1) ** 2 + (chartY - y1) ** 2);
            if (distance <= PIN_THRESHOLD) return drawing.id;
            break;
          }

          case "circle": {
            // Ellipse: check if point is inside OR near the edge
            const cx = (x1 + x2) / 2;
            const cy = (y1 + y2) / 2;
            const rx = Math.abs(x2 - x1) / 2;
            const ry = Math.abs(y2 - y1) / 2;
            if (rx < 2 && ry < 2) continue;

            // For very thin ellipses (long/flat), use distance to center line approach
            const aspectRatio =
              Math.max(rx, ry) / Math.max(1, Math.min(rx, ry));

            if (aspectRatio > 3) {
              // Very elongated ellipse - treat more like a line
              // Check distance to the major axis
              const distToCenter = Math.sqrt(
                (chartX - cx) ** 2 + (chartY - cy) ** 2,
              );
              const maxRadius = Math.max(rx, ry);
              if (distToCenter <= maxRadius + SHAPE_THRESHOLD) {
                // Also check if within the ellipse bounds (with generous padding)
                const normalizedX = (chartX - cx) / (rx + SHAPE_THRESHOLD * 2);
                const normalizedY = (chartY - cy) / (ry + SHAPE_THRESHOLD * 2);
                if (
                  normalizedX * normalizedX + normalizedY * normalizedY <=
                  1.5
                ) {
                  return drawing.id;
                }
              }
            } else {
              // Normal ellipse - check if inside with generous padding
              const normalizedX = (chartX - cx) / (rx + SHAPE_THRESHOLD);
              const normalizedY = (chartY - cy) / (ry + SHAPE_THRESHOLD);
              if (normalizedX * normalizedX + normalizedY * normalizedY <= 1) {
                return drawing.id;
              }
            }
            break;
          }

          case "rectangle": {
            // Rectangle: check if point is inside (with padding)
            const rectX = Math.min(x1, x2) - SHAPE_THRESHOLD;
            const rectY = Math.min(y1, y2) - SHAPE_THRESHOLD;
            const rectWidth = Math.abs(x2 - x1) + 2 * SHAPE_THRESHOLD;
            const rectHeight = Math.abs(y2 - y1) + 2 * SHAPE_THRESHOLD;

            if (
              chartX >= rectX &&
              chartX <= rectX + rectWidth &&
              chartY >= rectY &&
              chartY <= rectY + rectHeight
            ) {
              return drawing.id;
            }
            break;
          }

          case "line":
          case "arrow": {
            // Line/Arrow: check distance from the line segment
            const lineLength = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
            if (lineLength < 2) continue;

            // Calculate perpendicular distance to line
            const t = Math.max(
              0,
              Math.min(
                1,
                ((chartX - x1) * (x2 - x1) + (chartY - y1) * (y2 - y1)) /
                  (lineLength * lineLength),
              ),
            );
            const nearestX = x1 + t * (x2 - x1);
            const nearestY = y1 + t * (y2 - y1);
            const distance = Math.sqrt(
              (chartX - nearestX) ** 2 + (chartY - nearestY) ** 2,
            );

            if (distance <= LINE_THRESHOLD) return drawing.id;
            break;
          }

          case "text": {
            // Text: check if near the text position
            const distance = Math.sqrt((chartX - x1) ** 2 + (chartY - y1) ** 2);
            if (distance <= SHAPE_THRESHOLD + 25) return drawing.id;
            break;
          }

          default:
            break;
        }
      }

      return null;
    },
    [drawings],
  );

  // Crosshair mouse handlers - works anywhere on chart, not just on data points
  // Also handles drawing updates when in draw mode
  const handleMouseMove = useCallback(
    (e) => {
      if (e && e.chartX !== undefined && e.chartY !== undefined) {
        const { yScale } = axisScalesRef.current;

        let timestamp = null;
        let value = null;

        // For Y value, use scale invert
        if (yScale && typeof yScale.invert === "function") {
          value = yScale.invert(e.chartY);
        }

        // For X (timestamp), get it from activePayload if available,
        // otherwise try to find closest data point
        if (e.activePayload && e.activePayload.length > 0) {
          timestamp = e.activePayload[0]?.payload?.timestamp;
        } else if (e.activeLabel) {
          timestamp = e.activeLabel;
        }

        setCrosshair({
          x: e.chartX,
          y: e.chartY,
          timestamp,
          value,
          visible: true,
        });

        // Dismiss cluster tooltip when mouse moves away from the cluster.
        // Use distance check so tooltip stays while mouse is near the cluster,
        // but dismisses once the user has clearly moved away.
        if (clusterTooltip.visible && clusterTooltip.cluster) {
          const cl = clusterTooltip.cluster;
          const dx = e.chartX - cl.centerX;
          const dy = e.chartY - cl.centerY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          // Dismiss when mouse is more than 30px from cluster center
          if (dist > 30) {
            setClusterTooltip({ visible: false, x: 0, y: 0, cluster: null });
          }
        }

        // Update drawing if actively drawing - use data coordinates (timestamp, value)
        if (isDrawMode && isDrawing && timestamp !== null && value !== null) {
          updateDrawing(timestamp, value);
        }

        // Detect hover over drawings (for hover-to-delete)
        // Works in draw mode too, but not while actively dragging to create a drawing
        if (!isDrawing) {
          const hovered = findDrawingAtPoint(e.chartX, e.chartY);
          setHoveredDrawingId(hovered);
        }
      }
    },
    [isDrawing, updateDrawing, findDrawingAtPoint, clusterTooltip],
  );

  const handleMouseLeave = useCallback(() => {
    setCrosshair({
      x: null,
      y: null,
      timestamp: null,
      value: null,
      visible: false,
    });
    setHoveredDrawingId(null); // Clear hover when mouse leaves chart
    setClusterTooltip({ visible: false, x: 0, y: 0, cluster: null }); // Clear cluster tooltip
    // Don't clear marker tooltip here - let user dismiss via X or click outside
  }, []);

  // Handle mouse down for starting drawings
  // Extract timestamp from event and value from yScale.invert
  // For text tool, also pass pixel coordinates for input positioning
  const handleMouseDown = useCallback(
    (e) => {
      if (isDrawMode && e?.chartX !== undefined && e?.chartY !== undefined) {
        const { yScale } = axisScalesRef.current;

        // Get timestamp from activePayload or activeLabel (like crosshair does)
        let timestamp = null;
        if (e.activePayload && e.activePayload.length > 0) {
          timestamp = e.activePayload[0]?.payload?.timestamp;
        } else if (e.activeLabel) {
          timestamp = e.activeLabel;
        }

        // Get Y value using yScale.invert
        let value = null;
        if (yScale && typeof yScale.invert === "function") {
          value = yScale.invert(e.chartY);
        }

        if (timestamp !== null && value !== null) {
          // Pass pixel coordinates for text tool input positioning
          startDrawing(timestamp, value, e.chartX, e.chartY);
        }
      }
    },
    [isDrawMode, startDrawing],
  );

  // Handle mouse up for finishing drawings
  const handleMouseUp = useCallback(() => {
    if (isDrawMode && isDrawing) {
      finishDrawing();
    }
  }, [isDrawMode, isDrawing, finishDrawing]);

  // Handle Ask AI button click - opens modal
  const handleAskAI = useCallback(() => {
    if (drawings.length > 0) {
      setIsAskAIModalOpen(true);
    }
  }, [drawings.length]);

  // Handle Ask AI modal close
  const handleAskAIModalClose = useCallback(() => {
    setIsAskAIModalOpen(false);
  }, []);

  /**
   * Build a human-readable description of a drawing for the AI prompt.
   * Uses the rich coordinate data from serializeForAI().
   * Dates are displayed in user's local timezone with timezone indicator.
   */
  const formatDrawingDescription = useCallback((drawing, index) => {
    // Format with timezone using the helper that works without moment-timezone
    const formatDate = (isoString) => formatDateWithTimezone(isoString);
    const formatPrice = (price) =>
      `$${price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    let desc = `${index + 1}. `;

    switch (drawing.type) {
      case "pin":
        desc += `Pin marker at ${formatDate(drawing.position.datetime)}, ${formatPrice(drawing.position.price)}`;
        break;

      case "text":
        desc += `Text annotation: "${drawing.content}" at ${formatDate(drawing.position.datetime)}, ${formatPrice(drawing.position.price)}`;
        break;

      case "circle":
        desc += `Circle highlighting region:\n`;
        desc += `   - Time range: ${formatDate(drawing.bounds.leftDatetime)} to ${formatDate(drawing.bounds.rightDatetime)}\n`;
        desc += `   - Price range: ${formatPrice(drawing.bounds.bottomPrice)} to ${formatPrice(drawing.bounds.topPrice)}\n`;
        desc += `   - Center: ${formatDate(drawing.center.datetime)}, ${formatPrice(drawing.center.price)}`;
        break;

      case "rectangle":
        desc += `Rectangle highlighting region:\n`;
        desc += `   - Time range: ${formatDate(drawing.bounds.leftDatetime)} to ${formatDate(drawing.bounds.rightDatetime)}\n`;
        desc += `   - Price range: ${formatPrice(drawing.bounds.bottomPrice)} to ${formatPrice(drawing.bounds.topPrice)}`;
        break;

      case "arrow":
        desc += `Arrow (${drawing.direction}):\n`;
        desc += `   - From: ${formatDate(drawing.from.datetime)}, ${formatPrice(drawing.from.price)}\n`;
        desc += `   - To: ${formatDate(drawing.to.datetime)}, ${formatPrice(drawing.to.price)}`;
        break;

      case "line":
        desc += `Line:\n`;
        desc += `   - From: ${formatDate(drawing.from.datetime)}, ${formatPrice(drawing.from.price)}\n`;
        desc += `   - To: ${formatDate(drawing.to.datetime)}, ${formatPrice(drawing.to.price)}`;
        break;

      default:
        desc += `${drawing.type} drawing`;
    }

    if (drawing.note) {
      desc += `\n   - Note: "${drawing.note}"`;
    }

    return desc;
  }, []);

  // Handle Ask AI modal submission
  const handleAskAIModalSubmit = useCallback(
    ({ question }) => {
      // Build the data to send to AI Agent
      const drawingsData = serializeForAI();

      // Build viewport context - what portion of the chart is visible
      // Uses displayChartData for consistent indices with the chart
      const startIdx = zoomRange.startIndex ?? 0;
      const endIdx = zoomRange.endIndex ?? displayChartData.length - 1;
      const viewportStartTime = displayChartData[startIdx]?.timestamp;
      const viewportEndTime = displayChartData[endIdx]?.timestamp;

      const viewport = {
        startDatetime: viewportStartTime
          ? new Date(viewportStartTime).toISOString()
          : null,
        endDatetime: viewportEndTime
          ? new Date(viewportEndTime).toISOString()
          : null,
        priceRangeLow: yDomain[0],
        priceRangeHigh: yDomain[1],
      };

      // Build human-readable descriptions for each drawing
      const drawingDescriptions = drawingsData.drawings
        .map((d, i) => formatDrawingDescription(d, i))
        .join("\n\n");

      // Format viewport info for the prompt
      // Dates displayed in user's local timezone with timezone indicator
      const formatDate = (isoString) => formatDateWithTimezone(isoString);
      const formatPrice = (price) =>
        `$${price?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "N/A"}`;

      // Build backtest context header
      let backtestContext = "";
      if (strategyName || backtestVersion || backtestId) {
        backtestContext = "Backtest Context:\n";
        if (strategyName) backtestContext += `- Strategy: ${strategyName}\n`;
        if (backtestVersion)
          backtestContext += `- Version: ${backtestVersion}\n`;
        if (backtestId) backtestContext += `- Backtest ID: ${backtestId}\n`;
      }

      let viewportDesc = "";
      if (viewport.startDatetime && viewport.endDatetime) {
        viewportDesc = `Chart Viewport:\n`;
        viewportDesc += `- Visible time range: ${formatDate(viewport.startDatetime)} to ${formatDate(viewport.endDatetime)}\n`;
        viewportDesc += `- Visible price range: ${formatPrice(viewport.priceRangeLow)} to ${formatPrice(viewport.priceRangeHigh)}`;
      }

      // Build the full prompt
      let prompt = backtestContext;
      prompt += `\nI've marked ${drawings.length} area${drawings.length !== 1 ? "s" : ""} on the chart:\n`;
      prompt += viewportDesc;
      prompt += `\n\nMarked Areas:\n${drawingDescriptions}`;

      if (question) {
        prompt += `\n\nMy question: ${question}`;
      } else {
        prompt +=
          "\n\nPlease analyze these marked areas and explain what happened here.";
      }

      // Build the payload matching HistoryCard's "Fix Using AI" pattern
      const payload = {
        prompt,
        aiStrategyId,
        revisionId,
        source: "chart-drawings",
        context: {
          backtestId,
          viewport,
          drawings: drawingsData.drawings,
        },
      };

      // If parent provided onAskAI callback (e.g., we're on AI Agent page), use it directly
      // Otherwise, use the handoff pattern for navigation to AI Agent page
      // CRITICAL: Call the callback BEFORE any state changes to avoid re-render interference
      if (onAskAI) {
        onAskAI(payload);
      } else {
        // Set handoff for navigation (only when opening new tab)
        setAgentHandoff({
          ...payload,
          attachments: [],
        });
        window.open("/ai-agent", "_blank");
      }

      // Close the modal AFTER the callback to avoid React re-render timing issues
      setIsAskAIModalOpen(false);
    },
    [
      onAskAI,
      serializeForAI,
      drawings.length,
      aiStrategyId,
      revisionId,
      backtestId,
      backtestVersion,
      strategyName,
      zoomRange,
      displayChartData,
      yDomain,
      formatDrawingDescription,
    ],
  );

  // Handle "Explain this trade using AI" from marker tooltip
  const handleExplainTrade = useCallback(
    (tradeData) => {
      // Format the trade details for the AI prompt
      const timestamp = tradeData.chartTimestamp;
      const formattedDate = moment(timestamp).format("MMMM D, YYYY");
      const formattedTime = `${moment(timestamp).format("h:mm:ss A")} ${getTimezoneAbbr()}`;

      // Build comprehensive trade details
      let tradeDetails = [];
      tradeDetails.push(`Symbol: ${tradeData.symbol || "Unknown"}`);
      tradeDetails.push(
        `Side: ${(tradeData.side || tradeData.markerType || "").toUpperCase().replace(/_/g, " ")}`,
      );
      tradeDetails.push(`Date: ${formattedDate}`);
      tradeDetails.push(`Time: ${formattedTime}`);

      if (tradeData.price !== undefined) {
        tradeDetails.push(
          `Price: $${tradeData.price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        );
      }

      if (tradeData.assetType && tradeData.assetType !== "stock") {
        tradeDetails.push(`Asset Type: ${tradeData.assetType}`);
      }

      if (tradeData.assetStrike > 0) {
        tradeDetails.push(
          `Strike: $${tradeData.assetStrike.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        );
      }

      if (tradeData.assetRight) {
        tradeDetails.push(`Right: ${tradeData.assetRight}`);
      }

      if (tradeData.assetExpiration) {
        tradeDetails.push(
          `Expiration: ${moment(tradeData.assetExpiration).format("MMM D, YYYY")}`,
        );
      }

      if (tradeData.tradeDetails?.tradeQuantity) {
        tradeDetails.push(`Quantity: ${tradeData.tradeDetails.tradeQuantity}`);
      }

      if (
        tradeData.tradeDetails?.tradeCost &&
        tradeData.tradeDetails.tradeCost !== 0
      ) {
        tradeDetails.push(
          `Trade Cost: $${Math.abs(tradeData.tradeDetails.tradeCost).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        );
      }

      // Build backtest context header
      let backtestContext = "";
      if (strategyName || backtestVersion || backtestId) {
        backtestContext = "Backtest Context:\n";
        if (strategyName) backtestContext += `- Strategy: ${strategyName}\n`;
        if (backtestVersion)
          backtestContext += `- Version: ${backtestVersion}\n`;
        if (backtestId) backtestContext += `- Backtest ID: ${backtestId}\n`;
        backtestContext += "\n";
      }

      const prompt = `${backtestContext}Why was this trade placed?

Trade Details:
${tradeDetails.map((d) => `- ${d}`).join("\n")}

Do not modify or refine the code, just answer the question about why the strategy placed this trade at this time.`;

      // Build the payload matching HistoryCard's "Fix Using AI" pattern
      const payload = {
        prompt,
        aiStrategyId,
        revisionId,
        source: "chart-trade-explain",
        context: {
          backtestId,
          backtestVersion,
          strategyName,
          tradeData: {
            symbol: tradeData.symbol,
            side: tradeData.side,
            price: tradeData.price,
            timestamp: tradeData.chartTimestamp,
          },
        },
      };

      // If parent provided onAskAI callback (e.g., we're on AI Agent page), use it directly
      // Otherwise, use the handoff pattern for navigation to AI Agent page
      // CRITICAL: Call the callback BEFORE any state changes to avoid re-render interference
      if (onAskAI) {
        onAskAI(payload);
      } else {
        // Set handoff for navigation (only when opening new tab)
        setAgentHandoff({
          ...payload,
          attachments: [],
        });
        window.open("/ai-agent", "_blank");
      }

      // Close the tooltip AFTER the callback to avoid React re-render timing issues
      setMarkerTooltip({ visible: false, x: 0, y: 0, data: null });
    },
    [
      aiStrategyId,
      revisionId,
      backtestId,
      backtestVersion,
      strategyName,
      onAskAI,
    ],
  );

  // Handle "Explain this marker using AI" from marker tooltip (for non-trade markers)
  const handleExplainMarker = useCallback(
    (markerData) => {
      // Format the marker details for the AI prompt
      const timestamp = markerData.chartTimestamp;
      const formattedDate = moment(timestamp).format("MMMM D, YYYY");
      const formattedTime = `${moment(timestamp).format("h:mm:ss A")} ${getTimezoneAbbr()}`;

      // Build marker details
      let markerDetails = [];
      markerDetails.push(`Marker Name: ${markerData.name || "Unknown"}`);
      markerDetails.push(`Date: ${formattedDate}`);
      markerDetails.push(`Time: ${formattedTime}`);

      if (markerData.chartY !== undefined) {
        markerDetails.push(
          `Value: ${markerData.chartY.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`,
        );
      }

      // Support both snake_case (API) and camelCase (transformed) field names
      const detailText = markerData.detail_text || markerData.detailText;
      if (detailText) {
        markerDetails.push(
          `Details: ${detailText.replace(/<br\s*\/?>/gi, ", ")}`,
        );
      }

      // Build backtest context header
      let backtestContext = "";
      if (strategyName || backtestVersion || backtestId) {
        backtestContext = "Backtest Context:\n";
        if (strategyName) backtestContext += `- Strategy: ${strategyName}\n`;
        if (backtestVersion)
          backtestContext += `- Version: ${backtestVersion}\n`;
        if (backtestId) backtestContext += `- Backtest ID: ${backtestId}\n`;
        backtestContext += "\n";
      }

      const prompt = `${backtestContext}What does this marker indicate and why did the strategy place it here?

Marker Details:
${markerDetails.map((d) => `- ${d}`).join("\n")}

Do not modify or refine the code, just answer the question about what this marker represents and why it appeared at this point.`;

      // Build the payload matching HistoryCard's "Fix Using AI" pattern
      const payload = {
        prompt,
        aiStrategyId,
        revisionId,
        source: "chart-marker-explain",
        context: {
          backtestId,
          backtestVersion,
          strategyName,
          markerData: {
            name: markerData.name,
            value: markerData.chartY,
            timestamp: markerData.chartTimestamp,
          },
        },
      };

      // If parent provided onAskAI callback (e.g., we're on AI Agent page), use it directly
      // Otherwise, use the handoff pattern for navigation to AI Agent page
      // CRITICAL: Call the callback BEFORE any state changes to avoid re-render interference
      if (onAskAI) {
        onAskAI(payload);
      } else {
        // Set handoff for navigation (only when opening new tab)
        setAgentHandoff({
          ...payload,
          attachments: [],
        });
        window.open("/ai-agent", "_blank");
      }

      // Close the tooltip AFTER the callback to avoid React re-render timing issues
      setMarkerTooltip({ visible: false, x: 0, y: 0, data: null });
    },
    [
      aiStrategyId,
      revisionId,
      backtestId,
      backtestVersion,
      strategyName,
      onAskAI,
    ],
  );

  // Toggle indicator visibility
  const toggleIndicator = useCallback((name) => {
    setHiddenIndicators((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }, []);

  // Toggle marker visibility (by type: 'buy', 'sell', or marker name)
  const toggleMarker = useCallback((key) => {
    setVisibleMarkers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key); // Hide it
      } else {
        next.add(key); // Show it
      }
      return next;
    });
  }, []);

  // Toggle OHLC series visibility
  const toggleOhlc = useCallback((name) => {
    setHiddenOhlc((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }, []);

  // Smart number formatting - preserve natural precision
  const formatValue = useCallback((value) => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value !== "number") return String(value);

    // For large numbers (>1000), show with commas and up to 2 decimals
    if (Math.abs(value) >= 1000) {
      return value.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }

    // For smaller numbers, show natural precision (up to 4 decimals if needed)
    const str = value.toString();
    const decimalPart = str.split(".")[1];
    const naturalDecimals = decimalPart ? decimalPart.length : 0;

    return value.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: Math.min(naturalDecimals, 4),
    });
  }, []);

  // Custom tooltip - includes OHLC values extracted from the data point
  // since OHLC is rendered via <Customized> and not included in Recharts payload
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    const timestamp = label;
    const date = moment(timestamp);

    // Extract OHLC values from the full data point (not in Recharts payload
    // because OHLC is rendered via <Customized>, not <Line>/<Bar>)
    const dataPoint = payload[0]?.payload;
    const ohlcEntries = [];
    if (dataPoint) {
      const seen = new Set();
      Object.keys(dataPoint).forEach((key) => {
        if (key.startsWith("ohlc_") && key.endsWith("_close")) {
          const ohlcName = key.replace("ohlc_", "").replace("_close", "");
          if (!seen.has(ohlcName) && !hiddenOhlc.has(ohlcName)) {
            seen.add(ohlcName);
            ohlcEntries.push({
              name: ohlcName,
              open: dataPoint[`ohlc_${ohlcName}_open`],
              high: dataPoint[`ohlc_${ohlcName}_high`],
              low: dataPoint[`ohlc_${ohlcName}_low`],
              close: dataPoint[`ohlc_${ohlcName}_close`],
            });
          }
        }
      });
    }

    return (
      <div className="indicator-chart-tooltip">
        <div className="tooltip-date">
          {date.isValid() ? date.format("MMM D, YYYY h:mm A") : ""}
        </div>
        {/* OHLC values rendered first (most important for price action) */}
        {ohlcEntries.map((ohlc, idx) => (
          <div key={`ohlc-${idx}`} className="tooltip-ohlc-section">
            <div className="tooltip-ohlc-name">{ohlc.name}</div>
            <div className="tooltip-ohlc-grid">
              <span className="tooltip-ohlc-label">O:</span>
              <span className="tooltip-ohlc-value">
                {formatValue(ohlc.open)}
              </span>
              <span className="tooltip-ohlc-label">H:</span>
              <span className="tooltip-ohlc-value">
                {formatValue(ohlc.high)}
              </span>
              <span className="tooltip-ohlc-label">L:</span>
              <span className="tooltip-ohlc-value">
                {formatValue(ohlc.low)}
              </span>
              <span className="tooltip-ohlc-label">C:</span>
              <span className="tooltip-ohlc-value">
                {formatValue(ohlc.close)}
              </span>
            </div>
          </div>
        ))}
        {/* Line indicator values */}
        {payload
          .filter((entry) => !entry.payload?.isOhlcCandle)
          .map((entry, idx) => (
            <div key={idx} className="tooltip-row">
              <span className="tooltip-name" style={{ color: entry.color }}>
                {entry.name}:
              </span>
              <span className="tooltip-value">{formatValue(entry.value)}</span>
            </div>
          ))}
      </div>
    );
  };

  // Count markers for legend display - group by type AND name
  const buyCount = processedSignals.filter(
    (s) => s.markerType === "buy",
  ).length;
  const sellCount = processedSignals.filter(
    (s) => s.markerType === "sell",
  ).length;

  // Group other markers by their name for individual legend entries
  const markersByName = useMemo(() => {
    const groups = {};
    processedSignals.forEach((s) => {
      if (s.markerType === "marker" && s.name) {
        if (!groups[s.name]) {
          groups[s.name] = {
            name: s.name,
            count: 0,
            color: s.color || MARKER_COLORS.marker,
            symbol: s.symbol || "circle",
          };
        }
        groups[s.name].count++;
      }
    });
    return Object.values(groups);
  }, [processedSignals]);

  // Helper to get legend icon for a shape
  const getShapeIcon = (shape) => {
    const s = (shape || "circle").toLowerCase();
    switch (s) {
      case "star":
        return "★";
      case "diamond":
        return "◆";
      case "triangle":
      case "arrowup":
        return "▲";
      case "triangledown":
      case "arrowdown":
        return "▼";
      case "square":
        return "■";
      case "cross":
      case "x":
        return "✕";
      case "plus":
        return "+";
      case "circle":
      default:
        return "●";
    }
  };

  // Show empty state only if no data at all
  if (lineIndicators.length === 0 && ohlcIndicators.length === 0) {
    return (
      <div
        className="indicator-chart-container indicator-chart-empty"
        style={{ minHeight: height }}
      >
        <p>No indicator data available</p>
      </div>
    );
  }

  return (
    <div
      className={`indicator-chart-container ${compact ? "indicator-chart-compact" : ""} ${isDrawMode ? "draw-mode" : ""} ${needsSeparatePanes ? "multi-pane" : ""}`}
      style={{ minHeight: totalChartHeight }}
      ref={chartContainerRef}
    >
      {/* Header with symbol selector, controls, and collapsible legend */}
      <div className="chart-header">
        {/* Inline symbol selector - own row on mobile (handles 1 or 5000 assets) */}
        {symbolSelector}

        {/* Controls bar: tools on left, zoom on right (mobile splits into own row) */}
        <div className="chart-header-controls">
          <div className="chart-controls-left">
            {/* Legend toggle button - visible only on mobile via CSS */}
            <button
              className="chart-legend-toggle"
              onClick={() => setLegendExpanded(!legendExpanded)}
              aria-expanded={legendExpanded}
              title={legendExpanded ? "Hide legend" : "Show legend"}
            >
              Legend {legendExpanded ? "▾" : "▸"}
            </button>

            {/* Draw toolbar - for marking areas to ask AI about */}
            <DrawToolbar
              isDrawMode={isDrawMode}
              activeTool={activeTool}
              drawingCount={drawings.length}
              onEnterDrawMode={enterDrawMode}
              onExitDrawMode={exitDrawMode}
              onSelectTool={selectTool}
              onClearAll={clearAllDrawings}
              onAskAI={handleAskAI}
            />
          </div>

          {/* Zoom controls */}
          <div className="chart-controls">
            <button
              className="chart-control-btn"
              onClick={handleZoomIn}
              title="Zoom In"
            >
              +
            </button>
            <button
              className="chart-control-btn"
              onClick={handleZoomOut}
              title="Zoom Out"
            >
              −
            </button>
            <button
              className="chart-control-btn chart-control-reset"
              onClick={handleZoomReset}
              title="Reset Zoom"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Legend panel - always visible on desktop, collapsible on mobile */}
        <div
          className={`chart-legend ${legendExpanded ? "chart-legend--expanded" : ""}`}
        >
          {/* OHLC section - candlestick data */}
          {ohlcIndicators.length > 0 && (
            <div className="legend-group">
              <span className="legend-group-title">OHLC</span>
              <div className="legend-group-items">
                {ohlcIndicators.map((ohlc) => (
                  <div
                    key={ohlc.name}
                    className={`legend-item legend-item-ohlc ${hiddenOhlc.has(ohlc.name) ? "legend-item-hidden" : ""}`}
                    onClick={() => toggleOhlc(ohlc.name)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && toggleOhlc(ohlc.name)
                    }
                    title={`Click to ${hiddenOhlc.has(ohlc.name) ? "show" : "hide"} ${ohlc.name}`}
                    role="button"
                    tabIndex={0}
                  >
                    <FaChartBar className="legend-ohlc-icon" />
                    <span className="legend-label">
                      {ohlc.name} ({ohlc.points?.length || 0})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lines section */}
          {lineIndicators.length > 0 && (
            <div className="legend-group">
              <span className="legend-group-title">Lines</span>
              <div className="legend-group-items">
                {lineIndicators.map((ind, idx) => (
                  <div
                    key={ind.name}
                    className={`legend-item legend-item-line ${hiddenIndicators.has(ind.name) ? "legend-item-hidden" : ""}`}
                    onClick={() => toggleIndicator(ind.name)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && toggleIndicator(ind.name)
                    }
                    title={`Click to ${hiddenIndicators.has(ind.name) ? "show" : "hide"} ${ind.name}`}
                    role="button"
                    tabIndex={0}
                  >
                    <span
                      className="legend-line-swatch"
                      style={{
                        backgroundColor: LINE_COLORS[idx % LINE_COLORS.length],
                      }}
                    />
                    <span className="legend-label">{ind.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trades section (Buy/Sell) */}
          {(buyCount > 0 || sellCount > 0) && (
            <div className="legend-group">
              <span className="legend-group-title">Trades</span>
              <div className="legend-group-items">
                {buyCount > 0 && (
                  <div
                    className={`legend-item legend-item-marker ${!visibleMarkers.has("buy") ? "legend-item-hidden" : ""}`}
                    onClick={() => toggleMarker("buy")}
                    onKeyDown={(e) => e.key === "Enter" && toggleMarker("buy")}
                    title={`Click to ${visibleMarkers.has("buy") ? "hide" : "show"} Buy markers`}
                    role="button"
                    tabIndex={0}
                  >
                    <span className="legend-marker legend-marker-buy">▲</span>
                    <span className="legend-label">Buy ({buyCount})</span>
                  </div>
                )}
                {sellCount > 0 && (
                  <div
                    className={`legend-item legend-item-marker ${!visibleMarkers.has("sell") ? "legend-item-hidden" : ""}`}
                    onClick={() => toggleMarker("sell")}
                    onKeyDown={(e) => e.key === "Enter" && toggleMarker("sell")}
                    title={`Click to ${visibleMarkers.has("sell") ? "hide" : "show"} Sell markers`}
                    role="button"
                    tabIndex={0}
                  >
                    <span className="legend-marker legend-marker-sell">▼</span>
                    <span className="legend-label">Sell ({sellCount})</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Markers section (custom markers) */}
          {markersByName.length > 0 && (
            <div className="legend-group">
              <span className="legend-group-title">Markers</span>
              <div className="legend-group-items">
                {markersByName.map((marker) => (
                  <div
                    key={marker.name}
                    className={`legend-item legend-item-marker legend-item-secondary ${!visibleMarkers.has(marker.name) ? "legend-item-hidden" : ""}`}
                    onClick={() => toggleMarker(marker.name)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && toggleMarker(marker.name)
                    }
                    title={`Click to ${visibleMarkers.has(marker.name) ? "hide" : "show"} ${marker.name}`}
                    role="button"
                    tabIndex={0}
                  >
                    <span
                      className="legend-marker"
                      style={{ color: marker.color }}
                    >
                      {getShapeIcon(marker.symbol)}
                    </span>
                    <span className="legend-label">
                      {marker.name} ({marker.count})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chart - Uses displayChartData which applies LTTB downsampling for performance */}
      <ResponsiveContainer width="100%" height={priceChartHeight}>
        <ComposedChart
          data={displayChartData}
          margin={priceChartMargin}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="rgba(255,255,255,0.08)"
          />

          <XAxis
            dataKey="timestamp"
            type="number"
            scale="time"
            domain={["dataMin", "dataMax"]}
            tickFormatter={formatXAxis}
            tick={{ fill: "#888", fontSize: 11 }}
            stroke="rgba(255,255,255,0.15)"
            interval="preserveStartEnd"
            minTickGap={80}
          />

          <YAxis
            domain={yDomain}
            tickFormatter={formatYAxis}
            tick={{ fill: "#888", fontSize: 11 }}
            stroke="rgba(255,255,255,0.15)"
            width={priceChartYAxisWidth}
            tickCount={priceChartLayout.yTickCount}
            hide={priceChartLayout.hideYAxis}
            allowDecimals={false}
          />

          <Tooltip content={<CustomTooltip />} cursor={false} />

          {/* Crosshair with axis labels - rendered via Customized for access to scales */}
          <Customized
            component={({ xAxisMap, yAxisMap, width, height, offset }) => {
              // Capture axis scales for mouse position calculation
              const xAxis = xAxisMap ? Object.values(xAxisMap)[0] : null;
              const yAxis = yAxisMap ? Object.values(yAxisMap)[0] : null;

              if (xAxis?.scale) axisScalesRef.current.xScale = xAxis.scale;
              if (yAxis?.scale) axisScalesRef.current.yScale = yAxis.scale;

              // Also capture scales for the drawing system
              if (xAxis?.scale && yAxis?.scale) {
                captureScales(xAxis.scale, yAxis.scale);
              }

              if (
                !crosshair.visible ||
                crosshair.x === null ||
                crosshair.y === null
              ) {
                return null;
              }

              const chartLeft = offset?.left || 0;
              const chartTop = offset?.top || 0;
              const chartRight = (offset?.left || 0) + (offset?.width || width);
              const chartBottom =
                (offset?.top || 0) + (offset?.height || height);

              // Format the axis labels
              const dateLabel = crosshair.timestamp
                ? moment(crosshair.timestamp).format("M/D HH:mm")
                : "";
              const valueLabel =
                crosshair.value !== null
                  ? crosshair.value.toLocaleString("en-US", {
                      maximumFractionDigits: 2,
                    })
                  : "";

              return (
                <g
                  className="crosshair-layer"
                  style={{ pointerEvents: "none" }}
                >
                  {/* Vertical line */}
                  <line
                    x1={crosshair.x}
                    y1={chartTop}
                    x2={crosshair.x}
                    y2={chartBottom}
                    stroke="rgba(255,255,255,0.4)"
                    strokeDasharray="4 4"
                    pointerEvents="none"
                  />
                  {/* Horizontal line */}
                  <line
                    x1={chartLeft}
                    y1={crosshair.y}
                    x2={chartRight}
                    y2={crosshair.y}
                    stroke="rgba(255,255,255,0.4)"
                    strokeDasharray="4 4"
                    pointerEvents="none"
                  />
                  {/* X-axis label (date) - prominent blue styling for visibility */}
                  {dateLabel && (
                    <g
                      transform={`translate(${crosshair.x}, ${chartBottom + 5})`}
                    >
                      <rect
                        x={-35}
                        y={0}
                        width={70}
                        height={18}
                        fill="rgba(59, 130, 246, 0.95)"
                        stroke="rgba(255, 255, 255, 0.4)"
                        strokeWidth={1}
                        rx={3}
                      />
                      <text
                        x={0}
                        y={13}
                        textAnchor="middle"
                        fill="#fff"
                        fontSize={10}
                        fontWeight="500"
                        fontFamily="Monaco, Consolas, monospace"
                      >
                        {dateLabel}
                      </text>
                    </g>
                  )}
                  {/* Y-axis label (value) - prominent blue styling for visibility */}
                  {valueLabel && (
                    <g
                      transform={`translate(${chartLeft - 5}, ${crosshair.y})`}
                    >
                      <rect
                        x={-75}
                        y={-10}
                        width={70}
                        height={20}
                        fill="rgba(59, 130, 246, 0.95)"
                        stroke="rgba(255, 255, 255, 0.4)"
                        strokeWidth={1}
                        rx={3}
                      />
                      <text
                        x={-40}
                        y={5}
                        textAnchor="middle"
                        fill="#fff"
                        fontSize={10}
                        fontWeight="500"
                        fontFamily="Monaco, Consolas, monospace"
                      >
                        {valueLabel}
                      </text>
                    </g>
                  )}
                </g>
              );
            }}
          />

          {/* Indicator lines - only price-pane overlays (EMA, Bollinger, etc.)
              Separate-pane indicators (RSI, MACD) render in their own panes below */}
          {pricePaneIndicators.map((ind, idx) => {
            const color = LINE_COLORS[idx % LINE_COLORS.length];
            if (ind.renderAs === "scatter") {
              return (
                <Scatter
                  key={ind.name}
                  dataKey={ind.name}
                  name={ind.name}
                  fill={color}
                  line={false}
                  isAnimationActive={false}
                  hide={hiddenIndicators.has(ind.name)}
                />
              );
            }
            return (
              <Line
                key={ind.name}
                type="monotone"
                dataKey={ind.name}
                stroke={color}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                hide={hiddenIndicators.has(ind.name)}
              />
            );
          })}

          {/* OHLC Candlesticks rendered against the active time scale so Brush/zoom and grouped candles line up. */}
          <Customized
            component={
              <OhlcCandlesLayer ohlcScatterSeries={ohlcScatterSeries} />
            }
          />

          {/* Recharts 3 supports direct custom children, but ComposedChart can skip them in this integration. */}
          <Customized
            component={
              <CustomMarkersLayer
                processedSignals={processedSignals}
                signalsForClustering={signalsForClustering}
                shouldClusterSignals={shouldClusterSignals}
                zoomRange={zoomRange}
                displayChartDataLength={displayChartData.length}
                visibleMarkers={visibleMarkers}
                clusterRadius={PERFORMANCE_CONFIG.CLUSTER_RADIUS}
                setMarkerTooltip={setMarkerTooltip}
                handleClusterClick={handleClusterClick}
                handleClusterHover={handleClusterHover}
                handleClusterLeave={handleClusterLeave}
              />
            }
          />

          {/* User drawings layer - renders pins, circles, arrows, rectangles, lines */}
          <Customized
            component={(props) => (
              <ChartDrawLayer
                {...props}
                drawings={drawings}
                currentDrawing={currentDrawing}
                isDrawMode={isDrawMode}
                onRemoveDrawing={removeDrawing}
                hoveredDrawingId={hoveredDrawingId}
              />
            )}
          />

          {/* Brush for range selection - only show if enough data and zoom range is initialized */}
          {/* Uses displayChartData.length since Brush operates on the rendered data */}
          {priceChartLayout.showBrush &&
            displayChartData.length > 50 &&
            zoomRange.startIndex !== null &&
            zoomRange.endIndex !== null && (
              <Brush
                dataKey="timestamp"
                ariaLabel="Visible chart range"
                height={isCompactChrome ? 32 : 40}
                stroke="rgba(136, 132, 216, 0.5)"
                fill="rgba(30, 30, 50, 0.8)"
                tickFormatter={formatXAxis}
                onChange={handleBrushChange}
                travellerWidth={12}
                startIndex={zoomRange.startIndex}
                endIndex={zoomRange.endIndex}
              />
            )}
        </ComposedChart>
      </ResponsiveContainer>

      {/* Separate indicator panes (RSI, MACD, etc.) rendered below the price chart */}
      {needsSeparatePanes && visibleIndicatorPanes.length > 0 && (
        <div className="indicator-chart-separate-panes">
          {visibleIndicatorPanes.map((pane) => (
            <div key={pane.id} className="indicator-chart-pane">
              <div className="indicator-chart-pane-header">
                <span className="indicator-chart-pane-label">{pane.label}</span>
              </div>
              <ResponsiveContainer
                width="100%"
                height={INDICATOR_PANE_HEIGHT - 30}
              >
                <ComposedChart
                  data={visibleChartData}
                  margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.05)"
                  />

                  <XAxis
                    dataKey="timestamp"
                    type="number"
                    scale="time"
                    domain={["dataMin", "dataMax"]}
                    tickFormatter={formatXAxis}
                    tick={{ fill: "#888", fontSize: 10 }}
                    stroke="rgba(255,255,255,0.1)"
                    interval="preserveStartEnd"
                    minTickGap={80}
                    height={20}
                  />

                  <YAxis
                    domain={pane.domain}
                    tickFormatter={formatYAxis}
                    tick={{ fill: "#888", fontSize: 10 }}
                    stroke="rgba(255,255,255,0.1)"
                    width={60}
                    tickCount={4}
                    allowDecimals={false}
                  />

                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0)
                        return null;
                      const date = moment(label);
                      return (
                        <div className="indicator-chart-tooltip">
                          <div className="tooltip-date">
                            {date.isValid()
                              ? date.format("MMM D, YYYY h:mm A")
                              : ""}
                          </div>
                          {payload.map((entry, i) => (
                            <div key={i} className="tooltip-row">
                              <span
                                className="tooltip-name"
                                style={{ color: entry.color }}
                              >
                                {entry.name}:
                              </span>
                              <span className="tooltip-value">
                                {formatValue(entry.value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    }}
                    cursor={false}
                  />

                  {/* Reference lines (e.g., RSI 30/70, MACD 0) */}
                  {pane.referenceLines.map((rl, i) => (
                    <ReferenceLine
                      key={`ref-${i}`}
                      y={rl.y}
                      stroke={rl.color}
                      strokeDasharray={rl.strokeDasharray}
                      label={false}
                    />
                  ))}

                  {/* Indicator lines for this pane */}
                  {pane.indicators.map((ind, idx) => {
                    // Find original index in lineIndicators for consistent coloring
                    const originalIdx = lineIndicators.findIndex(
                      (li) => li.name === ind.name,
                    );
                    const colorIdx = originalIdx >= 0 ? originalIdx : idx;
                    const color = LINE_COLORS[colorIdx % LINE_COLORS.length];
                    if (ind.renderAs === "scatter") {
                      return (
                        <Scatter
                          key={ind.name}
                          dataKey={ind.name}
                          name={ind.name}
                          fill={color}
                          line={false}
                          isAnimationActive={false}
                          hide={hiddenIndicators.has(ind.name)}
                        />
                      );
                    }
                    return (
                      <Line
                        key={ind.name}
                        type="monotone"
                        dataKey={ind.name}
                        stroke={color}
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                        hide={hiddenIndicators.has(ind.name)}
                      />
                    );
                  })}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
      )}

      {/* Marker tooltip overlay */}
      {markerTooltip.visible && markerTooltip.data && (
        <>
          {/* Invisible backdrop to catch clicks outside tooltip */}
          <div
            className="marker-tooltip-backdrop"
            onClick={() =>
              setMarkerTooltip({ visible: false, x: 0, y: 0, data: null })
            }
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
            }}
          />
          <div
            className="marker-tooltip"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              left: markerTooltip.x + 10,
              top: markerTooltip.y - 10,
              transform: "translateY(-100%)",
              zIndex: 1000,
            }}
          >
            <div className="marker-tooltip-header">
              <span className="marker-tooltip-icon">
                {markerTooltip.data.markerType === "buy"
                  ? "🟢"
                  : markerTooltip.data.markerType === "sell"
                    ? "🔴"
                    : "🔵"}
              </span>
              <span className="marker-tooltip-title">
                {markerTooltip.data.markerType === "buy" ||
                markerTooltip.data.markerType === "sell"
                  ? (markerTooltip.data.side || markerTooltip.data.markerType)
                      .toUpperCase()
                      .replace(/_/g, " ")
                  : markerTooltip.data.name || "Marker"}
              </span>
              <button
                className="marker-tooltip-close"
                onClick={() =>
                  setMarkerTooltip({ visible: false, x: 0, y: 0, data: null })
                }
                aria-label="Close tooltip"
              >
                ×
              </button>
            </div>
            <div className="marker-tooltip-content">
              {/* For trades, show trade details */}
              {markerTooltip.data.markerType === "buy" ||
              markerTooltip.data.markerType === "sell" ? (
                <>
                  {/* Symbol */}
                  {markerTooltip.data.symbol && (
                    <div className="marker-tooltip-row">
                      <span className="marker-tooltip-label">Symbol:</span>
                      <span className="marker-tooltip-value">
                        {markerTooltip.data.symbol}
                      </span>
                    </div>
                  )}
                  {/* Asset type - only show if not a regular stock */}
                  {markerTooltip.data.assetType &&
                    markerTooltip.data.assetType !== "stock" && (
                      <div className="marker-tooltip-row">
                        <span className="marker-tooltip-label">Type:</span>
                        <span
                          className="marker-tooltip-value"
                          style={{ textTransform: "capitalize" }}
                        >
                          {markerTooltip.data.assetType}
                        </span>
                      </div>
                    )}
                  {/* Date and Time - shown early for context */}
                  <div className="marker-tooltip-row">
                    <span className="marker-tooltip-label">Date:</span>
                    <span className="marker-tooltip-value">
                      {moment(markerTooltip.data.chartTimestamp).format(
                        "MMM D, YYYY",
                      )}
                    </span>
                  </div>
                  <div className="marker-tooltip-row">
                    <span className="marker-tooltip-label">Time:</span>
                    <span className="marker-tooltip-value">
                      {moment(markerTooltip.data.chartTimestamp).format(
                        "h:mm A",
                      )}
                    </span>
                  </div>
                  {/* Price */}
                  {markerTooltip.data.price !== undefined && (
                    <div className="marker-tooltip-row">
                      <span className="marker-tooltip-label">Price:</span>
                      <span className="marker-tooltip-value">
                        $
                        {markerTooltip.data.price?.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  )}
                  {/* Strike price - only for options (must be positive number) */}
                  {markerTooltip.data.assetStrike > 0 && (
                    <div className="marker-tooltip-row">
                      <span className="marker-tooltip-label">Strike:</span>
                      <span className="marker-tooltip-value">
                        $
                        {markerTooltip.data.assetStrike.toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}
                      </span>
                    </div>
                  )}
                  {/* Call/Put - only for options */}
                  {markerTooltip.data.assetRight && (
                    <div className="marker-tooltip-row">
                      <span className="marker-tooltip-label">Right:</span>
                      <span
                        className="marker-tooltip-value"
                        style={{ textTransform: "capitalize" }}
                      >
                        {markerTooltip.data.assetRight}
                      </span>
                    </div>
                  )}
                  {/* Expiration - only for options/futures */}
                  {markerTooltip.data.assetExpiration && (
                    <div className="marker-tooltip-row">
                      <span className="marker-tooltip-label">Expiration:</span>
                      <span className="marker-tooltip-value">
                        {moment(markerTooltip.data.assetExpiration).format(
                          "MMM D, YYYY",
                        )}
                      </span>
                    </div>
                  )}
                  {/* Quantity */}
                  {markerTooltip.data.tradeDetails?.tradeQuantity && (
                    <div className="marker-tooltip-row">
                      <span className="marker-tooltip-label">Quantity:</span>
                      <span className="marker-tooltip-value">
                        {markerTooltip.data.tradeDetails.tradeQuantity}
                      </span>
                    </div>
                  )}
                  {/* Trade cost - only if non-zero */}
                  {markerTooltip.data.tradeDetails?.tradeCost !== undefined &&
                    markerTooltip.data.tradeDetails?.tradeCost !== null &&
                    markerTooltip.data.tradeDetails?.tradeCost !== 0 && (
                      <div className="marker-tooltip-row">
                        <span className="marker-tooltip-label">
                          Trade Cost:
                        </span>
                        <span className="marker-tooltip-value">
                          $
                          {Math.abs(
                            markerTooltip.data.tradeDetails.tradeCost,
                          ).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    )}
                  {/* Multiplier - only if > 1 (futures/options) */}
                  {(markerTooltip.data.multiplier > 1 ||
                    markerTooltip.data.assetMultiplier > 1) && (
                    <div className="marker-tooltip-row">
                      <span className="marker-tooltip-label">Multiplier:</span>
                      <span className="marker-tooltip-value">
                        {markerTooltip.data.multiplier ||
                          markerTooltip.data.assetMultiplier}
                        x
                      </span>
                    </div>
                  )}
                </>
              ) : (
                /* For other markers (not trades), show value, date, time */
                <>
                  {markerTooltip.data.chartY !== undefined && (
                    <div className="marker-tooltip-row">
                      <span className="marker-tooltip-label">Value:</span>
                      <span className="marker-tooltip-value">
                        {markerTooltip.data.chartY?.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 4,
                        })}
                      </span>
                    </div>
                  )}
                  <div className="marker-tooltip-row">
                    <span className="marker-tooltip-label">Date:</span>
                    <span className="marker-tooltip-value">
                      {moment(markerTooltip.data.chartTimestamp).format(
                        "MMM D, YYYY",
                      )}
                    </span>
                  </div>
                  <div className="marker-tooltip-row">
                    <span className="marker-tooltip-label">Time:</span>
                    <span className="marker-tooltip-value">
                      {moment(markerTooltip.data.chartTimestamp).format(
                        "h:mm A",
                      )}
                    </span>
                  </div>
                </>
              )}
              {/* Detail text - for markers with additional info */}
              {/* Support both snake_case (API) and camelCase (transformed) field names */}
              {(markerTooltip.data.detail_text ||
                markerTooltip.data.detailText) && (
                <div className="marker-tooltip-detail">
                  {(
                    markerTooltip.data.detail_text ||
                    markerTooltip.data.detailText
                  )
                    .split(/<br\s*\/?>/gi)
                    .map((line, i, arr) => (
                      <React.Fragment key={i}>
                        {line}
                        {i < arr.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                </div>
              )}
              {/* "Explain with AI" button - for all markers */}
              <div className="marker-tooltip-actions">
                {markerTooltip.data.markerType === "buy" ||
                markerTooltip.data.markerType === "sell" ? (
                  <button
                    className="marker-tooltip-ask-ai"
                    onClick={() => handleExplainTrade(markerTooltip.data)}
                    title="Ask AI to explain why this trade was placed"
                  >
                    <FaCommentDots /> Explain this trade using AI
                  </button>
                ) : (
                  <button
                    className="marker-tooltip-ask-ai"
                    onClick={() => handleExplainMarker(markerTooltip.data)}
                    title="Ask AI to explain what this marker indicates"
                  >
                    <FaCommentDots /> Explain this marker using AI
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Cluster tooltip - shown when hovering over clustered signals.
          Uses pointer-events:none so it never blocks chart interaction
          (crosshairs, clicks, zoom). Dismisses via onMouseLeave on the cluster SVG element. */}
      {clusterTooltip.visible && clusterTooltip.cluster && (
        <TradeClusterTooltip
          cluster={clusterTooltip.cluster}
          x={clusterTooltip.x}
          y={clusterTooltip.y}
        />
      )}

      {/* Text annotation input overlay */}
      {pendingTextDrawing && (
        <div
          className="text-annotation-input-overlay"
          style={{
            position: "absolute",
            left: pendingTextDrawing.pixelX || 100,
            top: pendingTextDrawing.pixelY || 100,
            zIndex: 1001,
          }}
        >
          <input
            type="text"
            className="text-annotation-input"
            placeholder="Enter text..."
            value={textInputValue}
            onChange={(e) => setTextInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && textInputValue.trim()) {
                confirmTextDrawing(textInputValue);
                setTextInputValue("");
              } else if (e.key === "Escape") {
                cancelTextDrawing();
                setTextInputValue("");
              }
            }}
            autoFocus
          />
          <div className="text-annotation-actions">
            <button
              className="text-annotation-btn text-annotation-confirm"
              onClick={() => {
                if (textInputValue.trim()) {
                  confirmTextDrawing(textInputValue);
                  setTextInputValue("");
                }
              }}
              title="Add text (Enter)"
            >
              ✓
            </button>
            <button
              className="text-annotation-btn text-annotation-cancel"
              onClick={() => {
                cancelTextDrawing();
                setTextInputValue("");
              }}
              title="Cancel (Escape)"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {!compact && (
        <div className="chart-tip">
          <span className="chart-tip-icon">💡</span>
          <div className="chart-tip-text">
            <strong>Tip:</strong> You can ask the AI to customize this chart by
            adding lines or markers.
            <div className="chart-tip-examples">
              Examples: "Add a line for the 50-day moving average" or "Add
              markers when RSI crosses above 70"
            </div>
          </div>
        </div>
      )}

      {/* Ask AI Modal */}
      <AskAIModal
        isOpen={isAskAIModalOpen}
        onClose={handleAskAIModalClose}
        onSubmit={handleAskAIModalSubmit}
        drawings={drawings}
      />
    </div>
  );
}

// React.memo prevents re-renders when parent re-renders (e.g., polling updates)
// without the chart's own props changing. This is the single biggest perf win
// because the AI Agent page polls every 5-30s causing cascading re-renders.
export default React.memo(IndicatorChart);
